"use client";

import { Users, Loader2, Filter, X, Search, FileSpreadsheet, Clock, PhoneOff, Calendar, Info, CheckCircle2, AlertCircle, Bookmark, Trash2, Sparkles, ListChecks, ChevronDown, Check, Star } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/app/_module/components/ui/tooltip";
import { useState, useEffect, useRef, useMemo, useCallback, cloneElement } from "react";

import { useToast } from "@/app/_module/hooks/use-toast";
import { ToastAction } from "@/app/_module/components/ui/toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { FilterPop } from "@/app/_module/components/ui/shell-popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/app/_module/components/ui/command";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { LeadStatus } from "@/app/_module/constants/leadStatus";
import { useAgentStatusBreakdown } from "../hooks/useAgentStatusBreakdown";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import { useUnassignedLeads, useAgentWorkload, buildUnassignedLeadsParams, type UnassignedLeadsFilters } from "../hooks/queries";
import { useAssignLead } from "../hooks/useAssignLead";
import { useUnassignLead } from "../hooks/useUnassignLead";

import { AssignmentActions } from "./AssignmentActions";
import { LeadRowTags } from "./LeadRowTags";
import { leadAssignmentService } from "../services/service";
import { SelectImportListModal } from "./SelectImportListModal";
import { usePipelineStages, useCustomFieldKeys, useCustomFieldValues } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { groupCustomFieldOptions, KEY_FIELD_CANONICALS, canonicalCustomFieldLabel } from "@/app/_module/utils/customFieldAliases";
import { useKeyFieldsOnly } from "@/app/_module/hooks/useKeyFieldsOnly";

// System lead dispositions/statuses for filtering
const SYSTEM_DISPOSITIONS = [
  { value: LeadStatus.NEW_LEAD, label: "Leads", color: "bg-blue-100 text-blue-800" },
  { value: LeadStatus.APPOINTMENT, label: "Appointment", color: "bg-cyan-600 text-cyan-100" },
  { value: LeadStatus.UWR, label: "Underwriting", color: "bg-amber-500 text-amber-100" },
  { value: LeadStatus.APPROVED, label: "Sold & Approved", color: "bg-emerald-600 text-emerald-100" },
  { value: LeadStatus.NO_SALE, label: "No Sale", color: "bg-red-600 text-red-100" },
  { value: LeadStatus.BAD_NUMBER, label: "Bad Number", color: "bg-red-500 text-red-100" },
  { value: LeadStatus.DO_NOT_CALL, label: "Do Not Call", color: "bg-amber-800 text-amber-100" },
  { value: LeadStatus.DECLINED, label: "Declined", color: "bg-gray-600 text-gray-100" },
  { value: LeadStatus.LAPSED, label: "Lapsed", color: "bg-gray-900 text-gray-100" },
  { value: LeadStatus.DISPOSED, label: "Disposed", color: "bg-gray-600 text-gray-100" },
];

// Carrier filter value meaning "has any policy": find sales wherever an agent filed them, across every
// pipeline (Mark Dusevic, 2026-10-19). ⚠️ MIRRORED in the backend lead-dashboard.service.ts.
const ANY_CARRIER = "__any__";
// A policy, but not in Sold & Approved / Underwriting / Declined / Lapsed — the sales an agent filed in the
// wrong column. Both sales options include household members and DNC leads (full book).
const MISPLACED_SALES = "__misplaced__";
const carrierLabel = (c?: string) =>
  c === ANY_CARRIER ? "Any carrier (has a policy)" : c === MISPLACED_SALES ? "Misplaced sales" : c;

// Assignment status options
const ASSIGNMENT_STATUS = [
  { value: "all", label: "All Leads" },
  { value: "unassigned", label: "Unassigned Only" },
  { value: "assigned", label: "Assigned Only" },
];

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  leadStat?: string;
  lastCall?: string;
  createdAt: string;
  state?: string;
  assignedToUserId?: string;
  assignedToName?: string;
  hasHousehold?: boolean;
  householdMemberCount?: number;
  listName?: string;
  uploadedByName?: string;
  /** Has a live policy: unassign keeps it with its agent unless sold/closed are included. */
  hasPolicy?: boolean;
  /** Next upcoming appointment (ISO) and who it's with. */
  nextAppointmentAt?: string | null;
  nextAppointmentWith?: string | null;
}

// Common US states with their codes
export const US_STATES = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "District of Columbia", code: "DC" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" }
];

// Helper: compute days ago from a date string
const getDaysAgo = (dateStr: string | undefined | null): number | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
};

// Helper: get lead age badge color
const getAgeBadge = (daysOld: number | null): { text: string; className: string } | null => {
  if (daysOld === null) return null;
  if (daysOld <= 1) return { text: 'Today', className: 'bg-green-50 text-green-700' };
  if (daysOld <= 7) return { text: `${daysOld}d`, className: 'bg-green-50 text-green-700' };
  if (daysOld <= 30) return { text: `${daysOld}d`, className: 'bg-yellow-50 text-yellow-700' };
  if (daysOld <= 60) return { text: `${daysOld}d`, className: 'bg-orange-50 text-orange-700' };
  return { text: `${daysOld}d`, className: 'bg-red-50 text-red-700' };
};

// Helper: get last call badge
const getLastCallBadge = (lastCall: string | undefined | null, createdAt: string): { text: string; className: string; icon: 'clock' | 'warning' } => {
  if (!lastCall) {
    const daysOld = getDaysAgo(createdAt);
    if (daysOld !== null && daysOld > 7) {
      return { text: 'Never called', className: 'bg-red-50 text-red-600', icon: 'warning' };
    }
    return { text: 'Never called', className: 'bg-gray-100 text-gray-500', icon: 'warning' };
  }
  const days = getDaysAgo(lastCall);
  if (days === null) return { text: 'Called', className: 'bg-gray-100 text-gray-500', icon: 'clock' };
  if (days === 0) return { text: 'Called today', className: 'bg-green-50 text-green-700', icon: 'clock' };
  if (days <= 3) return { text: `${days}d ago`, className: 'bg-green-50 text-green-700', icon: 'clock' };
  if (days <= 7) return { text: `${days}d ago`, className: 'bg-yellow-50 text-yellow-700', icon: 'clock' };
  if (days <= 30) return { text: `${days}d ago`, className: 'bg-orange-50 text-orange-700', icon: 'clock' };
  return { text: `${days}d ago`, className: 'bg-red-50 text-red-700', icon: 'clock' };
};

interface FilterPreset {
  id: string;
  name: string;
  /** Loaded on every fresh open of the page (one per user). */
  isDefault?: boolean;
  filters: {
    /** Rows per page at save time; absent on older presets. */
    pageSize?: number;
    state?: string; // legacy single-state presets
    states?: string[];
    timezone?: string;
    disposition?: string;
    assignmentStatus: string;
    listName?: string;
    agent?: string;
    agency?: string;
    carrier?: string;
    city?: string;
    minAge?: string;
    maxAge?: string;
    householdPrimaries: boolean;
    customFieldKey?: string;
    customFieldValues?: string[];
    customFieldMin?: string | number | null;
    customFieldMax?: string | number | null;
    /** Multi-condition custom-field set; absent on presets saved before it existed. */
    customFieldFilters?: Array<{ key: string; values?: string[]; min?: string | number | null; max?: string | number | null }>;
  };
}

const fetchFilterPresets = async (): Promise<FilterPreset[]> => {
  try {
    const res: any = await api.get({ url: getApiUrl('/lead-assignment/filter-presets'), auth: true });
    return res.data?.data || [];
  } catch { return []; }
};

const persistFilterPresets = async (presets: FilterPreset[]) => {
  try {
    await api.put({ url: getApiUrl('/lead-assignment/filter-presets'), auth: true, body: { presets } });
  } catch { /* silent fail — presets are non-critical */ }
};

interface UnassignedLeadsProps {
  leads?: Lead[];
  agents?: any[];
}

/**
 * Searchable filter dropdown (Popover + cmdk) — replaces plain Selects in the
 * Filters panel; rosters/lists run 50+ entries and need type-to-find.
 */
/**
 * Filter dropdown. Single-select by default; pass `values` + `onSelectMany` for
 * multi-select (the popover then stays OPEN so several can be ticked in one go).
 *
 * Multi mode was added for pipeline/disposition: owners triage by GROUPS of stages
 * ("everything still workable" = New lead + No answer + Callback), and one-at-a-time
 * meant re-opening the filter for every stage before every assignment.
 */
function FilterCombobox({
  value,
  onSelect,
  values,
  onSelectMany,
  allLabel,
  searchPlaceholder,
  options,
  wide,
  footer,
}: {
  value?: string;
  onSelect?: (v?: string) => void;
  values?: string[];
  onSelectMany?: (v: string[]) => void;
  allLabel: string;
  searchPlaceholder: string;
  options: { value: string; label: string; node?: React.ReactNode }[];
  /**
   * Break out of the trigger's width and lay the options out in COLUMNS.
   *
   * The dropdown is normally pinned to the trigger width, which is a narrow filter
   * box — fine for 6 dispositions, useless for a real agent's board. Mark Gurraj runs
   * 33 stages, so picking three of them meant scrolling a one-per-line list the whole
   * way down. Going sideways puts the whole set on screen at once.
   */
  wide?: boolean;
  /** Rendered under the scrolling list, inside the dropdown (e.g. a "show all fields" toggle). */
  footer?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const multi = Array.isArray(values) && typeof onSelectMany === 'function';
  const selected = options.find((o) => o.value === value);
  const isOn = (v: string) => (multi ? values!.includes(v) : value === v);
  const nothingSelected = multi ? values!.length === 0 : !value;

  const triggerLabel = (() => {
    if (!multi) return selected ? selected.node ?? selected.label : allLabel;
    if (values!.length === 0) return allLabel;
    if (values!.length === 1) {
      const one = options.find((o) => o.value === values![0]);
      return one ? one.node ?? one.label : values![0];
    }
    return `${values!.length} selected`;
  })();

  const toggle = (v: string) => {
    if (multi) {
      const next = values!.includes(v) ? values!.filter((x) => x !== v) : [...values!, v];
      onSelectMany!(next);
      // Stay open — picking several stages is the whole point of multi mode.
      return;
    }
    onSelect?.(v);
    setOpen(false);
  };

  // Shared between the desktop Popover and the shell's inline panel below.
  const commandBody = (
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className={wide ? "max-h-[420px]" : "max-h-[260px]"}>
            <CommandEmpty>Nothing found.</CommandEmpty>
            {/* 2 columns on small screens, 3 once there's room — cmdk still handles
                keyboard nav and filtering, the grid only changes the layout. */}
            {/* The grid has to target [cmdk-group-items], NOT CommandGroup.
                cmdk renders <div cmdk-group><div cmdk-group-heading/><div cmdk-group-items>…
                so putting `grid` on the group laid out the heading and the wrapper as
                columns and left the items stacked one-per-line — which is why it still
                looked cut off. Also unset the group's inherited overflow-hidden, which
                clipped the wider content. */}
            <CommandGroup
              className={
                wide
                  ? "!overflow-visible [&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:grid-cols-2 md:[&_[cmdk-group-items]]:grid-cols-3 [&_[cmdk-group-items]]:gap-x-2"
                  : undefined
              }
            >
              <CommandItem
                value={`zzz-all ${allLabel}`}
                onSelect={() => {
                  if (multi) { onSelectMany!([]); return; }
                  onSelect?.(undefined);
                  setOpen(false);
                }}
              >
                <Check className={`mr-2 h-4 w-4 ${nothingSelected ? "opacity-100" : "opacity-0"}`} />
                {allLabel}
              </CommandItem>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  onSelect={() => toggle(o.value)}
                >
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${isOn(o.value) ? "opacity-100" : "opacity-0"}`} />
                  {/* In wide mode a cell is ~230px, so `truncate` would clip real labels
                      like "Americo all states (3222)". Wrap instead — the row grows, the
                      text stays readable. Single-column mode keeps truncating. */}
                  <span className={wide ? "whitespace-normal break-words leading-tight" : "truncate"}>
                    {o.node ?? o.label}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {footer && <div className="border-t p-1">{footer}</div>}
        </Command>
  );

  // ⚠️ Shell fork: Radix popper anchors drift VERTICALLY in the WKWebView
  // (the same failure the carrier pickers had) — this panel opened ~60px
  // below its trigger, burying itself under the next filter. An inline
  // absolute panel under the trigger cannot drift. Desktop keeps the
  // Popover (portal + popper are fine in normal browsers).
  const isMobileShell =
    typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  if (isMobileShell) {
    return (
      <div className="relative">
        <Button
          variant="outline"
          role="combobox"
          onClick={() => setOpen((o) => !o)}
          className="w-full h-10 justify-between font-normal"
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
        {open && (
          <>
            {/* invisible scrim — outside tap closes without dimming */}
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden rounded-md border border-border bg-white shadow-lg">
              {commandBody}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full h-10 justify-between font-normal">
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={
          wide
            ? "p-0 w-[min(52rem,94vw)] [&_[cmdk-item]]:py-1.5 [&_[cmdk-item]]:items-start"
            : "p-0 w-[var(--radix-popover-trigger-width)]"
        }
        align="start"
      >
        {commandBody}
      </PopoverContent>
    </Popover>
  );
}

export const UnassignedLeads = function ({ leads, agents }: UnassignedLeadsProps) {
  const [stateFilters, setStateFilters] = useState<string[]>([]);
  const [stateSearch, setStateSearch] = useState("");
  const [timezoneFilter, setTimezoneFilter] = useState<string | undefined>(undefined);
  const [leadNameSearch, setLeadNameSearch] = useState<string>("");
  const [selectedLeadFilter, setSelectedLeadFilter] = useState<string | undefined>(undefined);
  // Multi-select: owners filter by a GROUP of stages (e.g. everything still workable).
  // Sent to the API as a comma-separated `status` — the backend accepts a list.
  const [dispositionFilters, setDispositionFilters] = useState<string[]>([]);
  const dispositionParam = dispositionFilters.length ? dispositionFilters.join(',') : undefined;
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState<string>("all");
  const [agentFilter, setAgentFilter] = useState<string | undefined>(undefined);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [carrierPickerOpen, setCarrierPickerOpen] = useState(false);
  // Multi-select, same as dispositions. Sent as a comma-separated filterAgencyId.
  const [agencyFilters, setAgencyFilters] = useState<string[]>([]);
  const agencyParam = agencyFilters.length ? agencyFilters.join(',') : undefined;
  const [listNameFilter, setListNameFilter] = useState<string | undefined>(undefined);
  const [carrierFilter, setCarrierFilter] = useState<string | undefined>(undefined);
  const [debouncedCarrierFilter, setDebouncedCarrierFilter] = useState<string | undefined>(undefined);
  const [cityFilter, setCityFilter] = useState<string | undefined>(undefined);
  const [debouncedCityFilter, setDebouncedCityFilter] = useState<string | undefined>(undefined);
  // Age range as typed; debouncedAge is what gets sent.
  const [ageMinStr, setAgeMinStr] = useState("");
  const [ageMaxStr, setAgeMaxStr] = useState("");
  const [debouncedAge, setDebouncedAge] = useState<{ min?: string; max?: string }>({});
  const [householdPrimariesFilter, setHouseholdPrimariesFilter] = useState(false);
  /**
   * Custom-field conditions already added, AND-ed with each other and with the
   * field being edited below. Agents were saving a pipeline per combination of
   * Carrier / Product Type / Premium because only one field could be filtered
   * at a time — this is that fix.
   */
  const [customFieldFilters, setCustomFieldFilters] = useState<
    Array<{ key: string; values?: string[]; min?: string | number | null; max?: string | number | null }>
  >([]);
  const [customFieldKey, setCustomFieldKey] = useState<string | undefined>(undefined);
  const [customFieldValues, setCustomFieldValues] = useState<string[]>([]);
  const [customFieldValueSearch, setCustomFieldValueSearch] = useState("");
  // Numeric-range mode on the custom field (mutually exclusive with values).
  const [cfMode, setCfMode] = useState<"values" | "range">("values");
  const [cfMinStr, setCfMinStr] = useState("");
  const [cfMaxStr, setCfMaxStr] = useState("");
  const [showLeadSuggestions, setShowLeadSuggestions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSelectImportModal, setShowSelectImportModal] = useState(false);
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>([]);
  const [presetName, setPresetName] = useState("");
  const [showPresetPopover, setShowPresetPopover] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50); // Default to 50 leads per page
  const [debouncedLeadName, setDebouncedLeadName] = useState<string | undefined>(undefined);
  const leadSearchRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cityDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const carrierDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const filtersRestoredRef = useRef(false);
  // Fetch saved filter presets from backend on mount. A starred default preset
  // is applied on a fresh open (the last-used restore below yields to it).
  useEffect(() => {
    fetchFilterPresets().then((presets) => {
      setFilterPresets(presets);
      // Default beats the last-used restore (which already ran — presets arrive
      // async). That is what "default" means: the page opens on it every time.
      const def = presets.find((p) => p.isDefault);
      if (def) {
        filtersRestoredRef.current = true;
        handleLoadPreset(def);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch pipeline stages to include custom pipelines in disposition filter
  const { data: pipelineStages } = usePipelineStages();

  // Custom-field filtering (same source/endpoints the leads page uses): all
  // discoverable custom-field keys, and the distinct values of the chosen key.
  const { data: customFieldKeys = [] } = useCustomFieldKeys();
  // Same grouping + "key fields only" default as the leads page (see LeadsSearch):
  // synonyms/typos merged, `_` keys dropped, Carrier / Annual / Monthly first.
  const groupedCustomFields = useMemo(() => groupCustomFieldOptions(customFieldKeys as any[]), [customFieldKeys]);
  const keyCustomFields = useMemo(() => groupedCustomFields.filter((f) => f.isKeyField), [groupedCustomFields]);
  const [keyFieldsOnly, setKeyFieldsOnly] = useKeyFieldsOnly();
  const keyOnlyActive = keyFieldsOnly && keyCustomFields.length > 0;
  const customFieldOptions = useMemo(() => {
    const pool = keyOnlyActive ? keyCustomFields : groupedCustomFields;
    // keep the chosen field visible even when the list is narrowed
    const withChosen = customFieldKey && !pool.some((f) => f.key === customFieldKey)
      ? [...pool, { key: customFieldKey, type: "text", isKeyField: false }] : pool;
    return withChosen.map((f) => ({ value: f.key, label: f.key }));
  }, [keyOnlyActive, keyCustomFields, groupedCustomFields, customFieldKey]);

  // Range bounds, kept RAW: a bound may be a number (50) or a date
  // ("01/01/2023", "2023-01-01", or a bare "2023"). Number() coerced dates to
  // NaN and silently dropped them, which is why an Effective Date range was
  // impossible to express. The server decides numeric-vs-date.
  const cfMin: string | null = cfMinStr.trim() !== "" ? cfMinStr.trim() : null;
  const cfMax: string | null = cfMaxStr.trim() !== "" ? cfMaxStr.trim() : null;
  const cfRangeActive = !!customFieldKey && cfMode === "range" && (cfMin != null || cfMax != null);
  const cfValuesActive = !!customFieldKey && cfMode === "values" && customFieldValues.length > 0;

  // Build disposition options: system statuses + custom pipeline stages
  // Stages that AGENTS have their leads sitting in — including stages from their own
  // custom pipelines, which this owner does not have and therefore could never filter by.
  //
  // usePipelineStages() only ever returns the CALLING user's stages (both the visible and
  // the /all endpoint are scoped to userId + agencyId), so before this the dropdown offered
  // "the pipeline you assigned from" and nothing else. If an agent moved a lead into a
  // column that exists only in their pipeline, the owner had no way to pull it back —
  // and 48,019 of Brandon's 91,297 assigned leads have been moved by their agent.
  // poll=false: we only need the LIST of stages, which changes rarely. Polling this
  // (~807ms on a big agency) every 60s just to populate a dropdown is not worth it.
  const { data: agentBreakdown } = useAgentStatusBreakdown(true, false);

  const LEAD_DISPOSITIONS = useMemo(() => {
    const mine = (pipelineStages || [])
      .filter((s) => !s.isSystem && s.isVisible)
      .map((s) => ({
        value: s.name,
        label: s.name,
        color: `text-white`,
        customColor: s.color,
      }));

    const known = new Set([
      ...SYSTEM_DISPOSITIONS.map((d) => d.value),
      ...mine.map((d) => d.value),
    ]);

    // Anything an agent is actually using that we don't already offer. Labelled with the
    // owning agent so it's obvious this is THEIR column, not a stage you forgot you had.
    const theirs = (agentBreakdown?.customStages || [])
      .filter((st) => st?.name && !known.has(st.name))
      .map((st) => {
        const who = (st.owners || []).slice(0, 2).join(', ');
        const more = (st.owners || []).length > 2 ? ` +${st.owners.length - 2}` : '';
        return {
          value: st.name,
          label: who ? `${st.name} — ${who}${more}` : st.name,
          color: 'text-white',
          customColor: '#64748b', // slate: visually distinct from your own stages
        };
      });

    // AGENT SELECTED → show only THAT agent's pipeline.
    //
    // agentBreakdown.agents is keyed by the same agent id agentFilter holds, and its
    // value is that agent's real stage → count map. So once you pick an agent the
    // dropdown becomes their board: only stages they actually have leads in, each with
    // the live count, instead of every stage in the whole hierarchy.
    if (agentFilter && agentBreakdown?.agents?.[agentFilter]) {
      const theirStages = agentBreakdown.agents[agentFilter];
      const byName = new Map(
        [...SYSTEM_DISPOSITIONS, ...mine].map((d) => [d.value, d as any]),
      );
      return Object.entries(theirStages)
        .filter(([, count]) => (count as number) > 0)
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .map(([stage, count]) => {
          const known = byName.get(stage);
          return {
            value: stage,
            label: `${stage} (${count})`,
            color: known?.color ?? 'text-white',
            // Their own column keeps the slate tint so it reads as theirs, not yours.
            customColor: known?.customColor ?? '#64748b',
          };
        });
    }

    return [...SYSTEM_DISPOSITIONS, ...mine, ...theirs];
  }, [pipelineStages, agentBreakdown, agentFilter]);

  // Switching agent re-scopes the stage list, so prune any selected stage that agent
  // has no leads in — otherwise the filter silently returns nothing and looks broken.
  useEffect(() => {
    if (!agentFilter || !agentBreakdown?.agents?.[agentFilter]) return;
    const valid = new Set(Object.keys(agentBreakdown.agents[agentFilter]));
    setDispositionFilters((prev) => {
      const next = prev.filter((d) => valid.has(d));
      return next.length === prev.length ? prev : next;
    });
  }, [agentFilter, agentBreakdown]);

  // Debounce lead name search for server-side filtering (300ms)
  const handleLeadNameChange = useCallback((value: string) => {
    setLeadNameSearch(value);
    setSelectedLeadFilter(undefined);
    setShowLeadSuggestions(false);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      const trimmed = value.trim();
      setDebouncedLeadName(trimmed.length >= 2 ? trimmed : undefined);
      setPage(1);
    }, 300);
  }, []);

  // Debounce city filter for server-side search
  useEffect(() => {
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => {
      const trimmed = cityFilter?.trim();
      setDebouncedCityFilter(trimmed && trimmed.length >= 2 ? trimmed : undefined);
      setPage(1);
    }, 300);
    return () => {
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    };
  }, [cityFilter]);

  // Debounce carrier filter for server-side search
  useEffect(() => {
    if (carrierDebounceRef.current) clearTimeout(carrierDebounceRef.current);
    carrierDebounceRef.current = setTimeout(() => {
      const trimmed = carrierFilter?.trim();
      setDebouncedCarrierFilter(trimmed && trimmed.length >= 2 ? trimmed : undefined);
      setPage(1);
    }, 300);
    return () => {
      if (carrierDebounceRef.current) clearTimeout(carrierDebounceRef.current);
    };
  }, [carrierFilter]);

  // Debounce the age range too, so typing "65" doesn't fetch "6" first
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedAge({ min: ageMinStr || undefined, max: ageMaxStr || undefined });
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [ageMinStr, ageMaxStr]);

  // Cleanup debounce timer and success animation timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (assignSuccessTimerRef.current) clearTimeout(assignSuccessTimerRef.current);
    };
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (leadSearchRef.current && !leadSearchRef.current.contains(event.target as Node)) {
        setShowLeadSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Pass all filters server-side for proper pagination with 10k+ leads.
  // This ONE object feeds both the page list and "Select first N / Select all"
  // (fetchAllLeadIds), through the same buildUnassignedLeadsParams — they used
  // to be built separately and drifted.
  const leadFilters: UnassignedLeadsFilters = {
    states: stateFilters.length ? stateFilters.join(",") : undefined,
    timezone: timezoneFilter,
    listName: listNameFilter,
    assignmentStatus: assignmentStatusFilter !== "all" ? assignmentStatusFilter : undefined,
    filterAgentId: agentFilter,
    status: dispositionParam,
    leadName: debouncedLeadName,
    filterAgencyId: agencyParam,
    householdPrimaries: householdPrimariesFilter || undefined,
    city: debouncedCityFilter || undefined,
    carrier: debouncedCarrierFilter || undefined,
    minAge: debouncedAge.min,
    maxAge: debouncedAge.max,
    customFieldKey: (cfValuesActive || cfRangeActive) ? customFieldKey : undefined,
    customFieldValues: cfValuesActive ? customFieldValues.join("|") : undefined,
    customFieldMin: cfRangeActive && cfMin != null ? String(cfMin) : undefined,
    customFieldMax: cfRangeActive && cfMax != null ? String(cfMax) : undefined,
    customFieldFilters: customFieldFilters.length ? JSON.stringify(customFieldFilters) : undefined,
  };
  const { data: unassignedData, isLoading, isFetching, error, refetch: refetchUnassigned } = useUnassignedLeads({
    ...leadFilters,
    page,
    limit,
  });


  // Always use query data (with filters)
  // For agency owners: show only their own leads (filtered in useUnassignedLeads hook)
  // For agents: show all agency leads (as they're assigned by owners)
  // For owners on assignment page: includes BOTH assigned and unassigned leads
  const rawQueryLeads = unassignedData?.unassignedLeads || [];

  const { data: agentWorkloadData, refetch: refetchAgents } = useAgentWorkload();

  // Use passed agents or fallback to agentWorkloadData
  const availableAgents = agents || agentWorkloadData?.agents || [];

  // Build unique agency list from agents for the agency filter dropdown
  /**
   * Agencies for the filter, NESTED under their parent rather than one flat A-Z list.
   *
   * A flat list is fine for three agencies and unusable for a real downline — MRFG's tree
   * is 37. Nesting makes "this owner and everyone under them" visible at a glance, which
   * is how people actually think about who they're filtering to.
   *
   * Parents that aren't themselves in the list (no agents assigned there, so they never
   * appear in availableAgents) are treated as roots — otherwise their children would be
   * orphaned and silently dropped.
   */
  const uniqueAgencies = useMemo(() => {
    const agencyMap = new Map<string, { id: string; name: string; parentId: string | null }>();
    (availableAgents || []).forEach((agent: any) => {
      if (agent.agencyName && agent.agencyId) {
        agencyMap.set(agent.agencyId, {
          id: agent.agencyId,
          name: agent.agencyName,
          parentId: agent.agencyParentId ?? null,
        });
      }
    });

    const all = Array.from(agencyMap.values());
    const childrenOf = new Map<string | null, typeof all>();
    for (const a of all) {
      // Treat an unknown parent as top-level so nothing goes missing.
      const key = a.parentId && agencyMap.has(a.parentId) ? a.parentId : null;
      if (!childrenOf.has(key)) childrenOf.set(key, []);
      childrenOf.get(key)!.push(a);
    }
    for (const list of childrenOf.values()) list.sort((x, y) => x.name.localeCompare(y.name));

    // Depth-first so each agency is immediately followed by its own sub-agencies.
    const ordered: { id: string; name: string; depth: number }[] = [];
    const seen = new Set<string>();
    const walk = (parentKey: string | null, depth: number) => {
      for (const a of childrenOf.get(parentKey) || []) {
        if (seen.has(a.id)) continue; // cycle guard
        seen.add(a.id);
        ordered.push({ id: a.id, name: a.name, depth });
        walk(a.id, depth + 1);
      }
    };
    walk(null, 0);
    // Anything a cycle kept out still gets listed rather than vanishing.
    for (const a of all) if (!seen.has(a.id)) ordered.push({ id: a.id, name: a.name, depth: 0 });
    return ordered;
  }, [availableAgents]);

  // Helper to get agent's agency name by user ID
  const getAgentAgencyName = (userId: string | undefined): string | undefined => {
    if (!userId) return undefined;
    const agent = availableAgents.find((a: any) => a.agentId === userId || a.id === userId);
    return agent?.agencyName;
  };

  // Compute lead suggestions based on search input
  const leadSuggestions = useMemo(() => {
    if (!leadNameSearch || leadNameSearch.length < 2) return [];
    const searchLower = leadNameSearch.toLowerCase();
    return rawQueryLeads
      .filter((lead: Lead) => lead.name?.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  }, [rawQueryLeads, leadNameSearch]);

  // All filters are server-side. Client-side: only selectedLeadFilter (for direct lead lookup).
  const leadsToShow = useMemo(() => {
    let filtered = rawQueryLeads;
    if (selectedLeadFilter) {
      filtered = filtered.filter((lead: Lead) => lead.id === selectedLeadFilter);
    }
    return filtered;
  }, [rawQueryLeads, selectedLeadFilter]);

  // Use server-side total for pagination (covers ALL matching leads, not just current page)
  const serverTotal = unassignedData?.total || 0;
  const serverTotalPages = unassignedData?.totalPages || 1;

  // Counts from current page (for header display)
  const assignedOnPage = leadsToShow.filter((lead: Lead) => lead.assignedToUserId).length;
  const unassignedOnPage = leadsToShow.filter((lead: Lead) => !lead.assignedToUserId).length;
  // Assign and unassign both go in batches of 2,000 (see BACKEND_BATCH_SIZE in
  // useAssignLead/useUnassignLead). The frontend transparently splits larger
  // selections into sequential batches with a progress toast, so the user-facing
  // selection ceiling is larger — only a safety limit to prevent pathological
  // multi-tens-of-thousands selections from a single "select all" click.
  // = the CSV/Excel import cap (MAX_IMPORT_ROWS in parseSpreadsheetClient), so a whole list can be selected at once.
  const MAX_BULK_SELECT = 25000;
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);
  // "Select first N" draft. A string so a half-typed "2" of "250" doesn't fire a
  // fetch; it only runs on the button or Enter. 250 is the common batch size.
  const [selectFirstDraft, setSelectFirstDraft] = useState("250");
  const { mutate: assignLeads, isPending: isAssigning } = useAssignLead();
  const { mutate: unassignLeads, isPending: isUnassigning } = useUnassignLead();
  const { user } = useUserContext();
  // Custom-field Values picker: only the values of the leads this table shows (same params as the list),
  // minus the field's own condition — the server leaves it out too; here it also keeps a tick from
  // re-fetching the list it was picked from.
  const valuesIsOwnerOrAdmin = ["owner", "agency_owner", "admin"].includes(user.currentAgency?.role ?? "") || user?.user?.userType === "admin";
  const valuesScope = useMemo(() => {
    const own = (customFieldKey ?? "").trim().toLowerCase();
    const others = customFieldFilters.filter((c) => c.key.trim().toLowerCase() !== own);
    const params = buildUnassignedLeadsParams({
      ...leadFilters,
      customFieldKey: undefined, customFieldValues: undefined, customFieldMin: undefined, customFieldMax: undefined,
      customFieldFilters: others.length ? JSON.stringify(others) : undefined,
    }, valuesIsOwnerOrAdmin);
    params.delete("page");
    params.delete("limit");
    return Object.fromEntries(params.entries());
    // leadFilters is rebuilt every render; its serialized params are the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(leadFilters), customFieldKey, JSON.stringify(customFieldFilters), valuesIsOwnerOrAdmin]);
  const { data: customFieldValueOptions = [] } = useCustomFieldValues(customFieldKey ?? null, valuesScope, "assignment");
  // "Uploaded by" is visible to AGENCY ADMINS ONLY — never owners, never agents.
  const viewerAgencyRole = user?.user?.agencyRole || user?.currentAgency?.role;
  const isAgencyAdmin = viewerAgencyRole === 'ADMIN' || viewerAgencyRole === 'admin';

  // Remember the selection size when the mutation fires so the overlay can
  // display "Unassigning 347 leads…" even though the API is a single bulk
  // transaction (no real-time progress to stream).
  const [processingLabel, setProcessingLabel] = useState<{ action: 'assign' | 'unassign'; count: number } | null>(null);
  useEffect(() => {
    if (!isAssigning && !isUnassigning) setProcessingLabel(null);
  }, [isAssigning, isUnassigning]);

  // Block tab close / refresh while a bulk op is in flight. Browsers show a
  // generic "changes you made may not be saved" prompt — exact wording can't
  // be customized, but the prompt itself is what stops a refresh mid-commit.
  useEffect(() => {
    if (!isAssigning && !isUnassigning) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isAssigning, isUnassigning]);

  // Keep a running map of each visible lead's current assignee so the reassign
  // warning can name the agent even for leads that aren't on the current page.
  useEffect(() => {
    for (const lead of leadsToShow as Lead[]) {
      if (lead.assignedToUserId) {
        assignmentInfoRef.current.set(lead.id, { userId: lead.assignedToUserId, name: (lead as any).assignedToName });
      } else {
        assignmentInfoRef.current.delete(lead.id);
      }
    }
  }, [leadsToShow]);

  // A change of working set (filter / list / state) is a fresh assignment
  // context — clear the just-assigned guard so deliberate reassignment in a new
  // view isn't over-filtered.
  useEffect(() => {
    recentlyAssignedRef.current = new Set();
  }, [stateFilters, timezoneFilter, listNameFilter, dispositionParam, assignmentStatusFilter, agentFilter, agencyParam, debouncedLeadName, customFieldKey, customFieldValues, cfMode, cfMinStr, cfMaxStr, customFieldFilters]);
  
  // Count how many selected leads are assigned vs unassigned.
  //
  // PERF: this used to be an unmemoized Array.from(selectedLeads).filter() with
  // a leadsToShow.find() INSIDE it — O(selected x page), re-run on every render.
  // Page size goes to 1000 and MAX_BULK_SELECT is 10000, so a select-all was up
  // to 10,000,000 string comparisons plus a Set->Array copy, synchronously, on
  // every checkbox click. /lead-assignment had the worst rageclick rate in the
  // app (107 rageclicks across 51 pageviews — PostHog, 14d): people clicked,
  // the main thread was busy, so they clicked again.
  //
  // Now a Map built once per page + O(selected) lookups, memoized.
  const leadsById = useMemo(() => {
    const m = new Map<string, Lead>();
    for (const l of leadsToShow as Lead[]) m.set(l.id, l);
    return m;
  }, [leadsToShow]);

  const selectedAssignedCount = useMemo(() => {
    let n = 0;
    for (const leadId of selectedLeads) {
      if (leadsById.get(leadId)?.assignedToUserId) n++;
    }
    return n;
  }, [selectedLeads, leadsById]);
  const selectedUnassignedCount = selectedLeads.size - selectedAssignedCount;
  const hasAssignedLeads = selectedAssignedCount > 0;
  const hasUnassignedLeads = selectedUnassignedCount > 0;
  const { toast } = useToast();
  const [showAssignSuccess, setShowAssignSuccess] = useState(false);
  const [assignSuccessCount, setAssignSuccessCount] = useState(0);
  const [skippedInfo, setSkippedInfo] = useState<{ count: number; names: string[] } | null>(null);
  const assignSuccessTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Guard against accidentally re-assigning leads that were JUST assigned this
  // session. For OWNERS, "Select All" fetches includeAssigned=true (see
  // fetchAllLeadIds), so the leads you just handed to agent A stay in the list —
  // clicking "Select All" again re-grabs them and the next assign silently moves
  // them onto agent B. (Prod incident: 500 leads → Frans, then the SAME 500 →
  // Joe 39s later, because the second "Select All (500)" picked up the just-
  // assigned leads.) We remember what was just assigned and (a) skip those ids
  // on Select-All, (b) warn before reassigning leads already owned by another agent.
  const recentlyAssignedRef = useRef<Set<string>>(new Set());
  // leadId -> { userId, name } of its current assignee, captured from whatever
  // lead rows we've seen (current page + across-pages fetch). Powers the
  // "already assigned to another agent" reassignment warning below.
  const assignmentInfoRef = useRef<Map<string, { userId?: string; name?: string }>>(new Map());

  // Build unique lead source/list names from current page data for the dropdown.
  // CLUSTER case/whitespace variants into a single option: imports often arrive
  // with inconsistent header casing, so "Monthly Premium" / "monthly premium" /
  // "MONTHLY PREMIUM" would otherwise show up as 3 separate options (Mark's
  // feedback). The backend already matches listName with ILIKE, so picking the
  // clustered label matches every casing. We keep the "nicest" casing as the
  // label (prefer mixed-case over ALL-CAPS / all-lower), and break ties by the
  // most frequent variant.
  const uniqueListNames = useMemo(() => {
    const isNicer = (s: string) => s !== s.toUpperCase() && s !== s.toLowerCase();
    const byKey = new Map<string, { label: string; count: number }>();
    (rawQueryLeads || []).forEach((lead: Lead) => {
      const raw = (lead.listName || '').trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, { label: raw, count: 1 });
      } else {
        existing.count += 1;
        // Upgrade the label to a nicer casing if this variant reads better.
        if (isNicer(raw) && !isNicer(existing.label)) existing.label = raw;
      }
    });
    return Array.from(byKey.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((v) => v.label);
  }, [rawQueryLeads]);

  // Fetch agency carriers for the filter dropdown
  const [uniqueCarriers, setUniqueCarriers] = useState<string[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const result: any = await handleApiCall(async () =>
          api.get({ url: getApiUrl('/agency-carriers'), auth: true })
        );
        const carriers = (result?.data || result || []) as any[];
        const names = carriers
          .map((c: any) => c.carrierName || c.carrier_name || c.name || '')
          .filter(Boolean)
          .sort();
        setUniqueCarriers([...new Set(names)]);
      } catch {}
    })();
  }, []);
  // Carrier names as written in the uploaded sheets (custom field "Carrier", CARREIR / COMPANY folded
  // in). Most uploaded leads have no policy, so the agency list above alone finds almost nothing.
  // Fetched the first time the picker opens; the server matches a pick against both.
  const [sheetCarriersWanted, setSheetCarriersWanted] = useState(false);
  useEffect(() => { if (carrierPickerOpen) setSheetCarriersWanted(true); }, [carrierPickerOpen]);
  const { data: sheetCarrierValues = [], isLoading: sheetCarriersLoading } = useCustomFieldValues(sheetCarriersWanted ? "Carrier" : null);
  const sheetCarriers = useMemo(() => {
    // Once each, case-insensitively: cmdk lowercases item values, so "MUTUAL OF OMAHA" next to
    // "Mutual of Omaha" would be two rows it can't tell apart. The match is case-insensitive anyway.
    const seen = new Set(uniqueCarriers.map((c) => c.toLowerCase()));
    return sheetCarrierValues.filter((v) => {
      const k = v.trim().toLowerCase();
      if (k.length < 2 || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [sheetCarrierValues, uniqueCarriers]);

  // Clear filters function
  const clearFilters = () => {
    setStateFilters([]);
    setTimezoneFilter(undefined);
    setLeadNameSearch("");
    setDebouncedLeadName(undefined);
    setSelectedLeadFilter(undefined);
    setDispositionFilters([]);
    setAssignmentStatusFilter("all");
    setAgentFilter(undefined);
    setAgencyFilters([]);
    setListNameFilter(undefined);
    setCarrierFilter(undefined);
    setCityFilter(undefined);
    setAgeMinStr("");
    setAgeMaxStr("");
    setHouseholdPrimariesFilter(false);
    setCustomFieldKey(undefined);
    setCustomFieldFilters([]);
    setCustomFieldValues([]);
    setCustomFieldValueSearch("");
    setCfMode("values");
    setCfMinStr("");
    setCfMaxStr("");
    setPage(1);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  };

  const hasActiveFilters = stateFilters.length > 0 || timezoneFilter || selectedLeadFilter || dispositionFilters.length > 0 || assignmentStatusFilter !== "all" || agentFilter || agencyFilters.length > 0 || listNameFilter || carrierFilter || cityFilter || ageMinStr || ageMaxStr || debouncedLeadName || householdPrimariesFilter || cfValuesActive || cfRangeActive || customFieldFilters.length > 0;

  // The active filter set, in the preset shape so save/restore share one codec.
  const currentFilters = (): FilterPreset["filters"] => ({
    states: stateFilters,
    timezone: timezoneFilter,
    disposition: dispositionParam,
    assignmentStatus: assignmentStatusFilter,
    listName: listNameFilter,
    agent: agentFilter,
    agency: agencyParam,
    carrier: carrierFilter,
    city: cityFilter,
    minAge: ageMinStr || undefined,
    maxAge: ageMaxStr || undefined,
    householdPrimaries: householdPrimariesFilter,
    customFieldKey: customFieldKey,
    customFieldValues: customFieldValues,
    customFieldMin: cfMode === "range" ? cfMin : null,
    customFieldMax: cfMode === "range" ? cfMax : null,
    // Multi-condition set — the reason presets stop multiplying.
    customFieldFilters,
    pageSize: limit,
  });

  // Filters used to reset to nothing on every visit (component state only), so
  // opening a lead and coming back meant rebuilding them. Remembered per user
  // in this browser; restored once on mount through the preset loader.
  const rememberKey = user?.user?.id ? `leadassign:filters:${user.user.id}` : null;
  useEffect(() => {
    if (filtersRestoredRef.current || !rememberKey) return;
    filtersRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(rememberKey);
      if (raw) handleLoadPreset({ id: "last", name: "last", filters: JSON.parse(raw) });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rememberKey]);
  const filtersJson = JSON.stringify(currentFilters());
  useEffect(() => {
    if (!filtersRestoredRef.current || !rememberKey) return;
    try { localStorage.setItem(rememberKey, filtersJson); } catch {}
  }, [filtersJson, rememberKey]);

  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    const preset: FilterPreset = {
      id: Date.now().toString(),
      name,
      filters: currentFilters(),
    };
    const updated = [...filterPresets, preset];
    setFilterPresets(updated);
    persistFilterPresets(updated);
    setPresetName("");
  };

  const handleLoadPreset = (preset: FilterPreset) => {
    const f = preset.filters;
    setStateFilters(f.states || (f.state ? [f.state] : []));
    setTimezoneFilter(f.timezone);
    // Saved presets predate multi-select and stored a single string. Accept both
    // shapes so an existing preset still loads instead of silently filtering nothing.
    setDispositionFilters(
      Array.isArray(f.disposition)
        ? f.disposition
        : f.disposition
          ? String(f.disposition).split(',').map((x: string) => x.trim()).filter(Boolean)
          : [],
    );
    setAssignmentStatusFilter(f.assignmentStatus || "all");
    setListNameFilter(f.listName);
    setAgentFilter(f.agent);
    // Presets predate multi-select — accept the old single-string shape too.
    setAgencyFilters(
      Array.isArray(f.agency)
        ? f.agency
        : f.agency
          ? String(f.agency).split(',').map((x: string) => x.trim()).filter(Boolean)
          : [],
    );
    setCarrierFilter(f.carrier);
    setCityFilter(f.city);
    setAgeMinStr(f.minAge ?? "");
    setAgeMaxStr(f.maxAge ?? "");
    setHouseholdPrimariesFilter(f.householdPrimaries || false);
    setCustomFieldKey(f.customFieldKey);
    setCustomFieldValues(f.customFieldValues || []);
    const hasRange = f.customFieldMin != null || f.customFieldMax != null;
    setCfMode(hasRange ? "range" : "values");
    setCfMinStr(f.customFieldMin != null ? String(f.customFieldMin) : "");
    setCfMaxStr(f.customFieldMax != null ? String(f.customFieldMax) : "");
    // Presets saved before multi-field support simply have none.
    setCustomFieldFilters(Array.isArray(f.customFieldFilters) ? f.customFieldFilters : []);
    if (f.pageSize && f.pageSize > 0) setLimit(f.pageSize);
    setPage(1);
    setShowPresetPopover(false);
  };

  // One preset can be the default: it loads on a fresh open instead of the
  // last-used filters. Exclusive — starring one un-stars the rest.
  const handleSetDefaultPreset = (id: string) => {
    const updated = filterPresets.map((p) => ({ ...p, isDefault: p.id === id ? !p.isDefault : false }));
    setFilterPresets(updated);
    persistFilterPresets(updated);
  };

  const handleDeletePreset = (id: string) => {
    const updated = filterPresets.filter(p => p.id !== id);
    setFilterPresets(updated);
    persistFilterPresets(updated);
  };

  // Auto-refresh handled by React Query's refetchInterval in useUnassignedLeads (30s) and useAgentWorkload (10s)
  
  const formatPhone = (phone: string) => {
    if (!phone) return 'N/A';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // Format as (xxx) xxx-xxxx
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits[0] === '1') {
      // Handle +1 prefix
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length > 10) {
      // Handle any extra digits by taking last 10
      const last10 = digits.slice(-10);
      return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
    }

    // Return original if not enough digits
    return phone;
  };

  // Normalize status display name (New Lead -> Leads)
  const getStatusDisplayName = (status: string): string => {
    const normalized = status?.toLowerCase();
    if (normalized === 'new lead' || normalized === 'new') {
      return 'Leads';
    }
    return status || 'Leads';
  };

  const getStatusColor = (status: string) => {
    const normalized = status?.toLowerCase();
    switch (normalized) {
      case 'sold & approved':
      case 'sold':
      case 'approved':
        return 'bg-emerald-600 text-emerald-100';
      case 'appt':
      case 'appointment':
      case 'scheduled':
        return 'bg-cyan-600 text-cyan-100';
      case 'underwriting':
      case 'under writing':
        return 'bg-amber-500 text-amber-100';
      case 'new lead':
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'bad number':
      case 'bad #':
        return 'bg-red-500 text-red-100';
      case 'no sale':
        return 'bg-red-600 text-red-100';
      case 'do not call':
        return 'bg-amber-800 text-amber-100';
      case 'disposed':
        return 'bg-gray-600 text-gray-100';
      default:
        return 'bg-gray-600 text-gray-100';
    }
  };

  // Get custom pipeline color for inline styling (hex colors from pipeline stages)
  const getCustomPipelineColor = (status: string): string | undefined => {
    const stage = (pipelineStages || []).find(
      (s) => !s.isSystem && s.name.toLowerCase() === status?.toLowerCase()
    );
    return stage?.color;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        {/* Skeleton table header */}
        <div className="flex items-center gap-4 mb-4 pb-3 border-b">
          <div className="animate-pulse rounded bg-gray-200 h-4 w-4" />
          <div className="animate-pulse rounded bg-gray-200 h-4 w-32" />
          <div className="animate-pulse rounded bg-gray-200 h-4 w-28" />
          <div className="animate-pulse rounded bg-gray-200 h-4 w-16" />
          <div className="animate-pulse rounded bg-gray-200 h-4 w-20" />
          <div className="animate-pulse rounded bg-gray-200 h-4 w-24" />
        </div>
        {/* Skeleton rows */}
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-3 border-b border-gray-50">
            <div className="animate-pulse rounded bg-gray-200 h-4 w-4" />
            <div className="animate-pulse rounded bg-gray-200 h-4 w-36" />
            <div className="animate-pulse rounded bg-gray-200 h-4 w-28" />
            <div className="animate-pulse rounded bg-gray-200 h-4 w-12" />
            <div className="animate-pulse rounded-full bg-gray-200 h-5 w-16" />
            <div className="animate-pulse rounded bg-gray-200 h-4 w-20" />
          </div>
        ))}
      </div>
    );
  }

  // const leads = unassignedData?.unassignedLeads || []; // Removed - using leadsToShow instead

  // Handle individual lead selection
  const handleLeadSelection = (leadId: string, checked: boolean) => {
    setSelectAllAcrossPages(false);
    setSelectedLeads(prev => {
      const newSelection = new Set(prev);
      if (checked) {
        if (newSelection.size >= MAX_BULK_SELECT) {
          toast({
            title: `Selection limit reached`,
            description: `You can select up to ${MAX_BULK_SELECT.toLocaleString()} leads at a time — assign these first, then continue.`,
            variant: "destructive",
          });
          return prev;
        }
        newSelection.add(leadId);
      } else {
        newSelection.delete(leadId);
      }
      return newSelection;
    });
  };

  // Handle select all - unions (or removes) the current page with the existing
  // selection. Without this, navigating to page 2 and clicking "Select All"
  // would replace the page-1 selection instead of adding to it — classic bug
  // on paginated checkbox lists.
  const handleSelectAll = (checked: boolean) => {
    setSelectAllAcrossPages(false);
    // Exclude leads assigned moments ago so a repeat "Select All" can't re-grab
    // and silently move them off the agent they were just assigned to.
    const pageIds = leadsToShow.map(lead => lead.id).filter(id => !recentlyAssignedRef.current.has(id));
    if (checked) {
      setSelectedLeads(prev => {
        const next = new Set(prev);
        let hitCap = false;
        for (const id of pageIds) {
          if (next.size >= MAX_BULK_SELECT) {
            hitCap = true;
            break;
          }
          next.add(id);
        }
        if (hitCap) {
          toast({
            title: `Selection capped at ${MAX_BULK_SELECT.toLocaleString()}`,
            description: `You've reached the ${MAX_BULK_SELECT.toLocaleString()}-lead bulk limit. Assign these, then continue with the next batch.`,
            variant: "default",
          });
        }
        return next;
      });
    } else {
      // Unchecking only removes the CURRENT page from selection. Leads selected
      // on other pages stay. If the user wants to clear everything, they use
      // the "Clear selection" link in the across-pages banner.
      setSelectedLeads(prev => {
        const next = new Set(prev);
        for (const id of pageIds) next.delete(id);
        return next;
      });
    }
  };

  // Fetch lead IDs across ALL pages, in the server's order, for the current
  // filters. `count` is the "Select first N" box; leave it out and you get the
  // MAX_BULK_SELECT cap (the "Select all" button). Either way this is the only
  // way to select past the current page — page size stays independent.
  const fetchAllLeadIds = async (count?: number) => {
    const target = Math.max(1, Math.min(count ?? MAX_BULK_SELECT, MAX_BULK_SELECT));
    setIsFetchingAllIds(true);
    try {
      // Ask for a few extra so that dropping the just-assigned leads below still
      // leaves a full `target` — otherwise "first 250" quietly returns 243 right
      // after an assign. Never above MAX_BULK_SELECT: that's the act-on cap.
      const fetchLimit = Math.min(target + recentlyAssignedRef.current.size, MAX_BULK_SELECT);

      const userType = user?.user?.userType;
      const agencyRole = user.currentAgency?.role;
      const isOwnerOrAdmin = agencyRole === 'owner' || agencyRole === 'agency_owner' || agencyRole === 'admin' || userType === 'admin';
      // Same builder and same filters as the page list, so a selection can't
      // contain leads the list wouldn't show.
      const queryParams = buildUnassignedLeadsParams({ ...leadFilters, page: 1, limit: fetchLimit }, isOwnerOrAdmin);

      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leadAssignment.getUnassignedLeads) + `?${queryParams.toString()}`,
          auth: true,
        });
        return response;
      });

      const responseData = (result as any).data || result;
      const leads = responseData.leads || responseData.items || [];
      // Capture each lead's current assignee for the reassign warning, and drop
      // leads we JUST assigned this session so a second "Select All" can't
      // re-grab and move them off that agent (the Frans→Joe overwrite).
      for (const lead of leads as any[]) {
        if (lead.assignedToUserId) {
          assignmentInfoRef.current.set(lead.id, { userId: lead.assignedToUserId, name: lead.assignedToName });
        }
      }
      const allIdsRaw = (leads as any[]).map((lead: any) => lead.id);
      const allIds = allIdsRaw.filter((id: string) => !recentlyAssignedRef.current.has(id)).slice(0, target);
      const skippedJustAssigned = allIdsRaw.length - allIds.length;
      setSelectedLeads(new Set(allIds));
      setSelectAllAcrossPages(true);
      if (skippedJustAssigned > 0) {
        toast({
          title: `Skipped ${skippedJustAssigned.toLocaleString()} just-assigned lead${skippedJustAssigned > 1 ? 's' : ''}`,
          description: `Leads you assigned moments ago were left out so they aren't accidentally moved to a different agent. Change a filter to reassign them.`,
          variant: "default",
        });
      }
      if (count != null && allIds.length < target) {
        toast({
          title: `Selected ${allIds.length.toLocaleString()}`,
          description: `Only ${allIds.length.toLocaleString()} lead${allIds.length === 1 ? '' : 's'} match the current filters — fewer than the ${target.toLocaleString()} you asked for.`,
          variant: "default",
        });
      } else if (count != null) {
        toast({
          title: `Selected first ${allIds.length.toLocaleString()}`,
          description: allIds.length > 1000
            ? `Assign will run in batches of 1,000 automatically — you'll see progress in the corner.`
            : `The first ${allIds.length.toLocaleString()} leads matching your filters are selected, across every page.`,
          variant: "default",
        });
      } else if (serverTotal > MAX_BULK_SELECT) {
        toast({
          title: `Selected first ${MAX_BULK_SELECT.toLocaleString()}`,
          description: `A single action is capped at ${MAX_BULK_SELECT.toLocaleString()} leads. Assign these, then click again to grab the next ${MAX_BULK_SELECT.toLocaleString()}.`,
          variant: "default",
        });
      } else if (allIds.length > 1000) {
        toast({
          title: `${allIds.length.toLocaleString()} leads selected`,
          description: `Assign will run in batches of 1,000 automatically — you'll see progress in the corner.`,
          variant: "default",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to select all leads. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsFetchingAllIds(false);
    }
  };

  // Handle assignment
  const handleAssign = (agentId: string, cleanSlate?: boolean) => {
    // Abuse guard: ignore rapid double-clicks / a second assign fired while one
    // is already in flight. Without this, clicking Assign fast queues multiple
    // concurrent POSTs (Sam's issue) — each re-runs the heavy assign + refetch,
    // deepening page lag and racing the selection state.
    if (isAssigning || isUnassigning) return;
    if (!agentId) {
      toast({
        title: "Error",
        description: "No agent selected. Please select an agent first.",
        variant: "destructive",
      });
      return;
    }
    
    if (selectedLeads.size === 0) {
      toast({
        title: "No leads selected",
        description: "Please select at least one lead to assign.",
        variant: "destructive",
      });
      return;
    }
    
    const selectedAgent = availableAgents.find(agent => agent.agentId === agentId || agent.id === agentId);
    const agentName = selectedAgent?.agentName || selectedAgent?.name || 'Unknown Agent';
    
    // useAssignLead expects LeadAssignmentRequest: { leadIds, agentId, assignedAgencyId }
    // But backend only needs { leadIds, agentId } - assignedAgencyId comes from JWT
    // We'll still pass it for type compatibility, but backend ignores it
    const agencyId = user.currentAgency?.agency?.id;
    if (!agencyId) {
      toast({
        title: "Error",
        description: "No current agency found. Please select an agency first.",
        variant: "destructive",
      });
      return;
    }
    
    // Warn before MOVING leads that already belong to a different agent. This is
    // the guard that stops the silent overwrite: re-selecting already-assigned
    // leads (e.g. ones you just handed to another agent) and assigning them
    // quietly reassigns them with no indication the previous agent lost them.
    const reassignFromOthers = Array.from(selectedLeads).reduce(
      (acc, id) => {
        const info = assignmentInfoRef.current.get(id);
        if (info?.userId && info.userId !== agentId) {
          acc.count += 1;
          if (info.name) acc.names.add(info.name);
        }
        return acc;
      },
      { count: 0, names: new Set<string>() },
    );
    if (reassignFromOthers.count > 0) {
      const names = Array.from(reassignFromOthers.names);
      const who =
        names.length === 0
          ? 'another agent'
          : names.length <= 2
            ? names.join(' and ')
            : `${names.slice(0, 2).join(', ')} and ${names.length - 2} other${names.length - 2 > 1 ? 's' : ''}`;
      const proceed = window.confirm(
        `${reassignFromOthers.count.toLocaleString()} of the ${selectedLeads.size.toLocaleString()} selected lead(s) are already assigned to ${who}.\n\n` +
          `Assigning to ${agentName} will MOVE them off ${who}, not give ${agentName} a separate copy.\n\nContinue?`,
      );
      if (!proceed) return;
    }

    const selectedLeadIds = Array.from(selectedLeads);
    // Collect lead statuses for optimistic byStatus updates
    const leadStatuses = selectedLeadIds.map(id => {
      const lead = leadsToShow.find((l: Lead) => l.id === id);
      return lead?.leadStat || 'New lead';
    });

    setProcessingLabel({ action: 'assign', count: selectedLeadIds.length });
    assignLeads(
      {
        leadIds: selectedLeadIds,
        agentId: agentId,
        assignedAgencyId: agencyId, // Passed for type compatibility, backend gets from JWT
        leadStatuses,
        cleanSlate,
      },
      {
        onSuccess: (response: any) => {
          // The server's numbers, not the selection's. A lead whose person this agent
          // already has is refused, and household members ride along — so one assign
          // used to read 1,376 in the toast, 1,366 in the banner, and gave the agent
          // 1,513 (Mark Dusevic, 2026-10-13). A retry of only refused leads said
          // "10 lead(s) assigned" with nothing assigned.
          const r = response?.data ?? response ?? {};
          const assignedCount = Number(r.assignedCount ?? selectedLeadIds.length) || 0;
          const householdAdded = Number(r.householdMembersAdded) || 0;
          const skippedIdSet = new Set<string>((Array.isArray(r.skippedLeadIds) ? r.skippedLeadIds : []).map(String));
          const undoLeadIds = selectedLeadIds.filter((id) => !skippedIdSet.has(id));
          if (assignedCount === 0) {
            const refused = Number(r.skippedCount) || selectedLeadIds.length;
            toast({
              title: "Nothing assigned",
              description: `${agentName} already has ${refused === 1 ? "this person" : `all ${refused.toLocaleString()} of these people`} under other leads, so these copies stay unassigned.`,
              duration: 8000,
            });
            setSelectedLeads(new Set());
            setSelectAllAcrossPages(false);
            return;
          }
          toast({
            title: "Leads assigned",
            description:
              `${assignedCount.toLocaleString()} lead(s) assigned to ${agentName}` +
              (householdAdded > 0
                ? `, plus ${householdAdded.toLocaleString()} household member${householdAdded === 1 ? "" : "s"} who go with them`
                : ""),
            variant: "default",
            duration: 8000,
            action: (
              <ToastAction altText="Undo assignment" onClick={() => {
                unassignLeads({ leadIds: undoLeadIds, context: { leadStatuses: leadStatuses } }, {
                  onSuccess: () => {
                    undoLeadIds.forEach(id => {
                      recentlyAssignedRef.current.delete(id);
                      assignmentInfoRef.current.delete(id);
                    });
                    toast({
                      title: "Assignment Undone",
                      description: `${undoLeadIds.length} lead(s) unassigned`,
                    });
                  },
                });
              }}>
                Undo
              </ToastAction>
            ),
          });
          // Track skipped leads info if agent already had some of these leads
          const skippedCount = Number(r.skippedCount) || 0;
          const skippedNames = r.skippedLeads || [];
          if (skippedCount > 0) {
            const namesArr = (Array.isArray(skippedNames) ? skippedNames : [skippedNames]).filter(Boolean);
            setSkippedInfo({ count: skippedCount, names: namesArr });
            const shown = namesArr.slice(0, 3);
            const more = Math.max(0, skippedCount - shown.length);
            setTimeout(() => {
              toast({
                title: `${skippedCount.toLocaleString()} skipped — ${agentName} already has these people`,
                description: shown.length
                  ? `${shown.join(', ')}${more > 0 ? ` +${more.toLocaleString()} more` : ''}. These copies stay unassigned.`
                  : `${agentName} already has them under other leads, so these copies stay unassigned.`,
                variant: "default",
                duration: 6000,
              });
            }, 500);
          } else {
            setSkippedInfo(null);
          }
          setSelectedLeads(new Set()); // Clear selection after assignment
          setSelectAllAcrossPages(false);
          // Remember these as just-assigned so a follow-up "Select All" won't
          // re-grab them and silently move them onto the next agent, and so the
          // reassign warning fires if they're selected again. Cleared when the
          // filter/working set changes (see effect above).
          undoLeadIds.forEach(id => {
            recentlyAssignedRef.current.add(id);
            assignmentInfoRef.current.set(id, { userId: agentId, name: agentName });
          });
          // Show success animation banner
          if (assignSuccessTimerRef.current) clearTimeout(assignSuccessTimerRef.current);
          setAssignSuccessCount(assignedCount + householdAdded);
          setShowAssignSuccess(true);
          assignSuccessTimerRef.current = setTimeout(() => {
            setShowAssignSuccess(false);
            setSkippedInfo(null);
          }, skippedCount > 0 ? 8000 : 2000);
          // Query invalidation + event dispatch handled by useAssignLead hook
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || 
                              error?.message || 
                              error?.error?.message ||
                              "Failed to assign leads. Please try again.";
          toast({
            title: "Assignment Failed",
            description: errorMessage,
            variant: "destructive",
          });
        },
      }
    );
  };

  // Handle unassignment — only unassigns currently assigned leads
  // For the confirm dialog: how many of the selected assigned leads are sold/closed.
  // Plain function on purpose: this sits below an early return, so it cannot be a hook.
  const previewUnassign = async () => {
    const ids = Array.from(selectedLeads).filter((leadId) => leadsToShow.find((l: Lead) => l.id === leadId)?.assignedToUserId);
    return leadAssignmentService.previewUnassign(ids);
  };

  const handleUnassign = (_cleanSlate?: boolean, includeClosed = false) => {
    // Abuse guard: no-op if an assign/unassign is already in flight (see handleAssign).
    if (isAssigning || isUnassigning) return;
    // Filter to only assigned leads
    const assignedLeadIds = Array.from(selectedLeads).filter(leadId => {
      const lead = leadsToShow.find((l: Lead) => l.id === leadId);
      return lead?.assignedToUserId;
    });

    if (assignedLeadIds.length === 0) {
      toast({
        title: "No assigned leads selected",
        description: "None of the selected leads are currently assigned.",
        variant: "destructive",
      });
      return;
    }

    // Collect lead statuses for optimistic byStatus updates
    const unassignStatuses = assignedLeadIds.map(id => {
      const lead = leadsToShow.find((l: Lead) => l.id === id);
      return lead?.leadStat || 'New lead';
    });

    // Capture previous assignments for undo
    const previousAssignments = assignedLeadIds.map(id => {
      const lead = leadsToShow.find((l: Lead) => l.id === id);
      return { leadId: id, agentId: lead?.assignedToUserId, agentName: lead?.assignedToName || 'Unknown' };
    });

    setProcessingLabel({ action: 'unassign', count: assignedLeadIds.length });
    unassignLeads({ leadIds: assignedLeadIds, includeClosed, context: { leadStatuses: unassignStatuses } }, {
      onSuccess: (result: any) => {
        // Sold/closed leads the server kept with their agent are not "unassigned" and need no undo.
        const keptIds = new Set<string>(result?.skippedClosedIds ?? []);
        const moved = previousAssignments.filter(p => !keptIds.has(p.leadId));
        if (moved.length === 0) return; // nothing moved: the hook's toast says why

        // Group by previous agent for undo
        const byAgent = moved.reduce((acc, p) => {
          if (p.agentId) {
            if (!acc[p.agentId]) acc[p.agentId] = { agentName: p.agentName, leadIds: [] };
            acc[p.agentId].leadIds.push(p.leadId);
          }
          return acc;
        }, {} as Record<string, { agentName: string; leadIds: string[] }>);

        toast({
          title: "Leads Unassigned",
          description: `${moved.length} lead(s) unassigned${keptIds.size ? ` · ${keptIds.size} sold/closed stayed with their agent` : ''}`,
          duration: 8000,
          action: (
            <ToastAction altText="Undo unassignment" onClick={() => {
              // Re-assign each group back to their previous agent
              const agencyId = user.currentAgency?.agency?.id;
              if (!agencyId) return;
              Object.entries(byAgent).forEach(([agentId, data]) => {
                assignLeads({
                  leadIds: data.leadIds,
                  agentId,
                  assignedAgencyId: agencyId,
                  leadStatuses: data.leadIds.map(() => 'New lead'),
                });
              });
              toast({
                title: "Unassignment Undone",
                description: `Leads re-assigned to previous agents`,
              });
            }}>
              Undo
            </ToastAction>
          ),
        });
        // These leads are now unassigned — drop them from the just-assigned
        // guard and assignee map so they can be freely (re)selected.
        assignedLeadIds.forEach(id => {
          recentlyAssignedRef.current.delete(id);
          assignmentInfoRef.current.delete(id);
        });
        setSelectedLeads(new Set()); // Clear selection after unassignment
        setSelectAllAcrossPages(false);
      },
      onError: (error: any) => {
        // Error handling - log to monitoring service in production
      },
    });
  };

  // Check if all currently displayed leads are selected (respects filters)
  const isAllSelected = leadsToShow.length > 0 && leadsToShow.every((lead: Lead) => selectedLeads.has(lead.id));
  const isIndeterminate = selectedLeads.size > 0 && !isAllSelected;

  return (
    <div className="flex flex-col gap-4">
    {showFilters && (
      <div className="bg-white rounded-xl shadow-sm border p-5 flex-shrink-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State{stateFilters.length > 0 ? ` (${stateFilters.length})` : ""}
              </label>
              <FilterPop
                onOpenChange={(o) => { if (!o) setStateSearch(""); }}
                className="w-[var(--radix-popover-trigger-width)] p-0 flex flex-col max-h-[320px]"
                trigger={
                  <button
                    type="button"
                    className="w-full h-10 flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <span className={stateFilters.length ? "text-foreground truncate" : "text-muted-foreground"}>
                      {stateFilters.length === 0
                        ? "All States"
                        : stateFilters.length <= 4
                          ? stateFilters.join(", ")
                          : `${stateFilters.slice(0, 4).join(", ")} +${stateFilters.length - 4}`}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
                  </button>
                }
              >
                  <div className="flex-shrink-0 p-2 border-b border-border flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                      <input
                        autoFocus
                        value={stateSearch}
                        onChange={(e) => setStateSearch(e.target.value)}
                        placeholder="Search states..."
                        className="w-full h-8 pl-7 pr-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                    {stateFilters.length > 0 && (
                      <button type="button" onClick={() => { setStateFilters([]); setPage(1); }} className="text-xs text-blue-600 hover:underline flex-shrink-0">Clear</button>
                    )}
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto p-1">
                    {US_STATES
                      .filter((s) => {
                        const q = stateSearch.trim().toLowerCase();
                        return !q || s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q);
                      })
                      .map((state) => {
                        const checked = stateFilters.includes(state.code);
                        return (
                          <button
                            type="button"
                            key={state.code}
                            onClick={() => {
                              setStateFilters((prev) => checked ? prev.filter((c) => c !== state.code) : [...prev, state.code]);
                              setPage(1);
                            }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left"
                          >
                            <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${checked ? "bg-primary border-primary" : "border-input"}`}>
                              {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                            </span>
                            <span className="flex-1 truncate">{state.name}</span>
                            <span className="text-xs text-muted-foreground">{state.code}</span>
                          </button>
                        );
                      })}
                  </div>
              </FilterPop>
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time Zone
              </label>
              <Select value={timezoneFilter || "all"} onValueChange={(value) => { setTimezoneFilter(value === "all" ? undefined : value); setPage(1); }}>
                <SelectTrigger className="w-full h-10">
                  <SelectValue placeholder="All Time Zones" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">All Time Zones</SelectItem>
                  <SelectItem value="EST">Eastern (ET)</SelectItem>
                  <SelectItem value="CST">Central (CT)</SelectItem>
                  <SelectItem value="MST">Mountain (MT)</SelectItem>
                  <SelectItem value="PST">Pacific (PT)</SelectItem>
                  <SelectItem value="AKST">Alaska (AKT)</SelectItem>
                  <SelectItem value="HST">Hawaii (HT)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                Lead List
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      Filter by the name you gave your CSV import. This is the tag assigned when you uploaded leads.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>
              <FilterCombobox
                value={listNameFilter}
                onSelect={(v) => { setListNameFilter(v); setPage(1); }}
                allLabel="All Lead Lists"
                searchPlaceholder="Search lead lists..."
                options={uniqueListNames.map((name) => ({ value: name, label: name }))}
              />
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Disposition
              </label>
              <FilterCombobox
                values={dispositionFilters}
                onSelectMany={(v) => { setDispositionFilters(v); setPage(1); }}
                wide
                allLabel="All Dispositions"
                searchPlaceholder="Search dispositions..."
                options={LEAD_DISPOSITIONS.map((disposition) => ({
                  value: disposition.value,
                  label: disposition.label,
                  node:
                    'customColor' in disposition && disposition.customColor ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white" style={{ backgroundColor: disposition.customColor }}>
                        {disposition.label}
                      </span>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${disposition.color}`}>
                        {disposition.label}
                      </span>
                    ),
                }))}
              />
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assignment Status
              </label>
              <Select value={assignmentStatusFilter} onValueChange={(value) => { setAssignmentStatusFilter(value); setPage(1); }}>
                <SelectTrigger className="w-full h-10">
                  <SelectValue placeholder="All Leads" />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNMENT_STATUS.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {uniqueAgencies.length > 0 && (
              <div className="flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agency
                </label>
                <FilterCombobox
                  values={agencyFilters}
                  onSelectMany={(v) => { setAgencyFilters(v); setPage(1); }}
                  wide
                  allLabel="All Agencies"
                  searchPlaceholder="Search agencies..."
                  options={uniqueAgencies.map((agency: any) => ({
                    value: agency.id,
                    label: agency.name,
                    // Indent by depth; the search box still matches on the bare name.
                    node: (
                      <span
                        className={agency.depth > 0 ? 'text-gray-600' : 'font-medium'}
                        style={{ paddingLeft: agency.depth * 14 }}
                      >
                        {agency.depth > 0 && <span className="text-gray-300 mr-1">└</span>}
                        {agency.name}
                      </span>
                    ),
                  }))}
                />
              </div>
            )}

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent
              </label>
              {/* Searchable agent picker (rosters run 50+, a plain select is unusable) */}
              <FilterPop
                open={agentPickerOpen}
                onOpenChange={setAgentPickerOpen}
                className="p-0 w-[var(--radix-popover-trigger-width)]"
                trigger={
                  <Button variant="outline" role="combobox" className="w-full h-10 justify-between font-normal">
                    <span className="truncate">
                      {(() => {
                        if (!agentFilter) return "All Agents";
                        const a = (availableAgents || []).find((x: any) => (x.agentId || x.id) === agentFilter);
                        return a ? `${a.agentName || a.name}${a.agencyName ? ` (${a.agencyName})` : ""}` : "All Agents";
                      })()}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                  </Button>
                }
              >
                  <Command>
                    <CommandInput placeholder="Search agents..." />
                    <CommandList className="max-h-[260px]">
                      <CommandEmpty>No agents found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all agents"
                          onSelect={() => { setAgentFilter(undefined); setPage(1); setAgentPickerOpen(false); }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${!agentFilter ? "opacity-100" : "opacity-0"}`} />
                          All Agents
                        </CommandItem>
                        {(availableAgents || []).map((agent: any) => {
                          const id = agent.agentId || agent.id;
                          const label = `${agent.agentName || agent.name}${agent.agencyName ? ` (${agent.agencyName})` : ""}`;
                          return (
                            <CommandItem
                              key={id}
                              value={label}
                              onSelect={() => { setAgentFilter(id); setPage(1); setAgentPickerOpen(false); }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${agentFilter === id ? "opacity-100" : "opacity-0"}`} />
                              <span className="truncate">{label}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
              </FilterPop>
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Carrier
              </label>
              {/* Searchable carrier picker (same pattern as the Agent filter) */}
              <FilterPop
                open={carrierPickerOpen}
                onOpenChange={setCarrierPickerOpen}
                className="p-0 w-[var(--radix-popover-trigger-width)]"
                trigger={
                  <Button variant="outline" role="combobox" className="w-full h-10 justify-between font-normal">
                    <span className="truncate">{carrierLabel(carrierFilter) || "All Carriers"}</span>
                    <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                  </Button>
                }
              >
                  <Command>
                    <CommandInput placeholder="Search carriers..." />
                    <CommandList className="max-h-[260px]">
                      <CommandEmpty>No carriers found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all carriers"
                          onSelect={() => { setCarrierFilter(undefined); setPage(1); setCarrierPickerOpen(false); }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${!carrierFilter ? "opacity-100" : "opacity-0"}`} />
                          All Carriers
                        </CommandItem>
                        <CommandItem
                          value="any carrier has a policy sold sales"
                          onSelect={() => { setCarrierFilter(ANY_CARRIER); setPage(1); setCarrierPickerOpen(false); }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${carrierFilter === ANY_CARRIER ? "opacity-100" : "opacity-0"}`} />
                          {carrierLabel(ANY_CARRIER)}
                        </CommandItem>
                        <CommandItem
                          value="misplaced sales policy wrong stage"
                          onSelect={() => { setCarrierFilter(MISPLACED_SALES); setPage(1); setCarrierPickerOpen(false); }}
                        >
                          <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${carrierFilter === MISPLACED_SALES ? "opacity-100" : "opacity-0"}`} />
                          <div className="min-w-0">
                            <div>{carrierLabel(MISPLACED_SALES)}</div>
                            <div className="text-xs text-muted-foreground">
                              Has a policy, but not in Sold &amp; Approved, Underwriting, Declined or Lapsed
                            </div>
                          </div>
                        </CommandItem>
                        {uniqueCarriers.map((carrier) => (
                          <CommandItem
                            key={carrier}
                            value={carrier}
                            onSelect={() => { setCarrierFilter(carrier); setPage(1); setCarrierPickerOpen(false); }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${carrierFilter === carrier ? "opacity-100" : "opacity-0"}`} />
                            <span className="truncate">{carrier}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      {(sheetCarriersLoading || sheetCarriers.length > 0) && (
                        <CommandGroup heading="From your lead sheets">
                          {sheetCarriersLoading ? (
                            <CommandItem disabled value="loading carriers from your lead sheets">
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Loading…
                            </CommandItem>
                          ) : (
                            sheetCarriers.map((carrier) => (
                              <CommandItem
                                key={`sheet-${carrier}`}
                                value={carrier}
                                onSelect={() => { setCarrierFilter(carrier); setPage(1); setCarrierPickerOpen(false); }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${carrierFilter === carrier ? "opacity-100" : "opacity-0"}`} />
                                <span className="truncate">{carrier}</span>
                              </CommandItem>
                            ))
                          )}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
              </FilterPop>
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={cityFilter || ''}
                onChange={(e) => { setCityFilter(e.target.value || undefined); setPage(1); }}
                placeholder="Filter by city..."
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
              />
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Age
              </label>
              <div className="flex items-center gap-1">
                <Input
                  type="text" inputMode="numeric" placeholder="Min" aria-label="Minimum age"
                  value={ageMinStr}
                  onChange={(e) => { if (/^\d{0,3}$/.test(e.target.value)) setAgeMinStr(e.target.value); }}
                  className="h-10 flex-1 min-w-0 px-2 border-gray-300"
                />
                <span className="text-gray-400 text-xs">–</span>
                <Input
                  type="text" inputMode="numeric" placeholder="Max" aria-label="Maximum age"
                  value={ageMaxStr}
                  onChange={(e) => { if (/^\d{0,3}$/.test(e.target.value)) setAgeMaxStr(e.target.value); }}
                  className="h-10 flex-1 min-w-0 px-2 border-gray-300"
                />
              </div>
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Household
              </label>
              {/* Short text + min-h: these columns run ~120-170px (3 across half the
                  page), so "Household Primaries Only" spilled out of a fixed h-10 box.
                  If it still wraps, the box grows instead. */}
              <button
                type="button"
                aria-pressed={householdPrimariesFilter}
                aria-label="Household primaries only"
                onClick={() => { setHouseholdPrimariesFilter((v) => !v); setPage(1); }}
                className={`min-h-10 w-full rounded-md border px-2 py-1 text-sm font-medium leading-tight flex items-center justify-center gap-1.5 transition-colors ${
                  householdPrimariesFilter
                    ? "bg-purple-600 border-purple-600 text-white hover:bg-purple-700"
                    : "bg-background border-input text-gray-700 hover:bg-gray-50"
                }`}
              >
                Primaries only
                {householdPrimariesFilter && <Check className="h-4 w-4" />}
              </button>
            </div>

            {/* Last box so it can take the slack: without Agency there are 11,
                one short of filling the 2/3-column grid, so it spans 2. */}
            <div className={`flex flex-col relative ${uniqueAgencies.length > 0 ? "" : "sm:col-span-2"}`} ref={leadSearchRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lead Name
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={selectedLeadFilter ? rawQueryLeads.find((l: Lead) => l.id === selectedLeadFilter)?.name || leadNameSearch : leadNameSearch}
                  onChange={(e) => handleLeadNameChange(e.target.value)}
                  placeholder="Search lead name..."
                  className="w-full h-10 pl-9 pr-8 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
                />
                {(leadNameSearch || selectedLeadFilter) && (
                  <button
                    onClick={() => {
                      setLeadNameSearch("");
                      setSelectedLeadFilter(undefined);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {/* Suggestions dropdown */}
              {showLeadSuggestions && leadSuggestions.length > 0 && !selectedLeadFilter && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-[250px] overflow-y-auto">
                  {leadSuggestions.map((lead: Lead) => (
                    <button
                      key={lead.id}
                      onClick={() => {
                        setSelectedLeadFilter(lead.id);
                        setLeadNameSearch(lead.name);
                        setShowLeadSuggestions(false);
                        setPage(1);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between text-sm border-b border-gray-100 last:border-b-0"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{lead.name}</div>
                        <div className="text-xs text-gray-500">{formatPhone(lead.phone)} {lead.state && `• ${lead.state}`}</div>
                      </div>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${getCustomPipelineColor(lead.leadStat || '') ? 'text-white' : getStatusColor(lead.leadStat || 'new')}`}
                        style={getCustomPipelineColor(lead.leadStat || '') ? { backgroundColor: getCustomPipelineColor(lead.leadStat || '') } : undefined}
                      >
                        {lead.leadStat || 'New'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Field filter — pick a field, then check the values to keep
                (same fields/values source as the leads-page filter).
                Own full-width row: the 12 boxes above fill the 2/3-column grid
                exactly; as a 13th cell it left 2 empty slots beside it. */}
            {customFieldKeys.length > 0 && (
              <div className="sm:col-span-2 lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Field{customFieldKey && customFieldValues.length > 0 ? ` (${customFieldValues.length})` : ""}
                  </label>
                  <FilterCombobox
                    value={customFieldKey}
                    onSelect={(v) => { setCustomFieldKey(v); setCustomFieldValues([]); setCustomFieldValueSearch(""); setCfMode("values"); setCfMinStr(""); setCfMaxStr(""); setPage(1); }}
                    allLabel="No field"
                    searchPlaceholder="Search custom fields..."
                    // Guard: empty/whitespace keys (blank CSV header cells) crashed
                    // Radix Select with value="" — Sam Chehab white-screen, 2026-07-10.
                    options={customFieldOptions}
                    footer={keyCustomFields.length > 0 && groupedCustomFields.length > keyCustomFields.length ? (
                      <button
                        type="button"
                        onClick={() => setKeyFieldsOnly(!keyOnlyActive)}
                        className="w-full rounded px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-100"
                      >
                        {keyOnlyActive
                          ? `Show all fields (${groupedCustomFields.length - keyCustomFields.length} more)`
                          : "Key fields only"}
                        <span className="block text-[11px] text-gray-500">
                          {keyOnlyActive
                            ? KEY_FIELD_CANONICALS.map((c) => canonicalCustomFieldLabel(c) ?? c).join(", ")
                            : `Showing all ${groupedCustomFields.length}`}
                        </span>
                      </button>
                    ) : undefined}
                  />
                </div>
                {/* Greyed out until a field is picked. Values/Range sits on the label
                    line so the picker keeps the whole column; beside the toggle it was
                    squeezed to nothing at ~120px. h-5 pins it to label height: the
                    ≤1024px 44px tap-target rule grows the buttons, not the line. */}
                <fieldset disabled={!customFieldKey} className={`min-w-0 ${customFieldKey ? "" : "opacity-50"}`}>
                  <div className="flex items-center gap-1.5 h-5 mb-1 text-sm">
                    <button
                      type="button"
                      aria-pressed={cfMode !== "range"}
                      onClick={() => { setCfMode("values"); setCfMinStr(""); setCfMaxStr(""); setPage(1); }}
                      className={cfMode === "range" ? "text-gray-400 enabled:hover:text-gray-700" : "font-medium text-gray-700"}
                    >Values</button>
                    <span className="text-gray-300">/</span>
                    <button
                      type="button"
                      aria-pressed={cfMode === "range"}
                      onClick={() => { setCfMode("range"); setCustomFieldValues([]); setPage(1); }}
                      className={cfMode === "range" ? "font-medium text-gray-700" : "text-gray-400 enabled:hover:text-gray-700"}
                    >Range</button>
                  </div>
                  {cfMode === "range" ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="text" inputMode="decimal" placeholder="Min ≥"
                        value={cfMinStr}
                        onChange={(e) => { const r = e.target.value; if (r !== "" && !/^-?\d*\.?\d*$/.test(r)) return; setCfMinStr(r); setPage(1); }}
                        className="h-10 flex-1 min-w-0 px-2 border-gray-300"
                      />
                      <span className="text-gray-400 text-xs">–</span>
                      <Input
                        type="text" inputMode="decimal" placeholder="Max ≤"
                        value={cfMaxStr}
                        onChange={(e) => { const r = e.target.value; if (r !== "" && !/^-?\d*\.?\d*$/.test(r)) return; setCfMaxStr(r); setPage(1); }}
                        className="h-10 flex-1 min-w-0 px-2 border-gray-300"
                      />
                    </div>
                  ) : (
                    <FilterPop
                      onOpenChange={(o) => { if (!o) setCustomFieldValueSearch(""); }}
                      align="end"
                      className="w-64 p-0 flex flex-col max-h-[320px]"
                      trigger={
                        <button
                          type="button"
                          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm flex items-center justify-between gap-1 min-w-0 enabled:hover:bg-gray-50"
                        >
                          <span className="truncate">{customFieldValues.length ? `${customFieldValues.length} value${customFieldValues.length > 1 ? "s" : ""}` : "Select values"}</span>
                          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                        </button>
                      }
                    >
                        <div className="flex-shrink-0 p-2 border-b border-border flex items-center gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                            <input
                              autoFocus
                              value={customFieldValueSearch}
                              onChange={(e) => setCustomFieldValueSearch(e.target.value)}
                              placeholder="Search values..."
                              className="w-full h-8 pl-7 pr-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </div>
                          {customFieldValues.length > 0 && (
                            <button type="button" onClick={() => { setCustomFieldValues([]); setPage(1); }} className="text-xs text-blue-600 hover:underline flex-shrink-0">Clear</button>
                          )}
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-1">
                          {customFieldValueOptions.length === 0 ? (
                            <div className="py-4 text-center text-xs text-muted-foreground">No values for this field.</div>
                          ) : (
                            customFieldValueOptions
                              .filter((v) => { const q = customFieldValueSearch.trim().toLowerCase(); return !q || String(v).toLowerCase().includes(q); })
                              .map((v) => {
                                const checked = customFieldValues.includes(v);
                                return (
                                  <button
                                    type="button"
                                    key={v}
                                    onClick={() => { setCustomFieldValues((prev) => checked ? prev.filter((x) => x !== v) : [...prev, v]); setPage(1); }}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left"
                                  >
                                    <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${checked ? "bg-primary border-primary" : "border-input"}`}>
                                      {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                                    </span>
                                    <span className="flex-1 truncate">{v}</span>
                                  </button>
                                );
                              })
                          )}
                        </div>
                    </FilterPop>
                  )}
                </fieldset>
                {/* Commit the edited condition and reset the picker so another
                    field can be stacked. The editor's own value stays applied
                    without pressing this, so single-field use is unchanged. */}
                <button
                  type="button"
                  disabled={!customFieldKey || (!cfValuesActive && !cfRangeActive)}
                  onClick={() => {
                    if (!customFieldKey) return;
                    setCustomFieldFilters([
                      ...customFieldFilters,
                      {
                        key: customFieldKey,
                        ...(cfValuesActive ? { values: customFieldValues } : {}),
                        ...(cfRangeActive && cfMin != null ? { min: cfMin } : {}),
                        ...(cfRangeActive && cfMax != null ? { max: cfMax } : {}),
                      },
                    ].slice(0, 10));
                    setCustomFieldKey(undefined);
                    setCustomFieldValues([]);
                    setCustomFieldValueSearch("");
                    setCfMode("values");
                    setCfMinStr("");
                    setCfMaxStr("");
                    setPage(1);
                  }}
                  className="min-h-10 px-2 py-1 leading-tight sm:col-span-2 lg:col-span-1 rounded-md border border-dashed text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  + Add another field
                </button>
                {/* Conditions already added — each AND-ed with the others and
                    with the field being edited above. */}
                {customFieldFilters.length > 0 && (
                  <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap gap-1.5 rounded-md border bg-gray-50 p-1.5">
                    {customFieldFilters.map((c, i) => {
                      const label = c.values?.length
                        ? `${c.key}: ${c.values.slice(0, 2).join(", ")}${c.values.length > 2 ? ` +${c.values.length - 2}` : ""}`
                        : `${c.key}: ${c.min != null ? `≥${c.min}` : ""}${c.min != null && c.max != null ? " " : ""}${c.max != null ? `≤${c.max}` : ""}`;
                      return (
                        <span key={`${c.key}-${i}`} className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                          {label}
                          <button
                            type="button"
                            aria-label={`Remove ${c.key} filter`}
                            onClick={() => { setCustomFieldFilters(customFieldFilters.filter((_, j) => j !== i)); setPage(1); }}
                            className="hover:text-blue-600"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Saved Filter Presets */}
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-3">
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Bookmark className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Presets</span>
            </div>
            {filterPresets.length === 0 && !hasActiveFilters ? (
              <span className="text-xs text-gray-400 italic">Set filters above, then save them here</span>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap flex-1">
                {filterPresets.map((preset) => (
                  <div key={preset.id} className="group inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-md text-xs hover:border-primary hover:shadow-sm cursor-pointer transition-all">
                    <button onClick={() => handleLoadPreset(preset)} className="text-gray-700 group-hover:text-primary font-medium">
                      {preset.name}
                    </button>
                    <button
                      onClick={() => handleSetDefaultPreset(preset.id)}
                      title={preset.isDefault ? "Default — loads when you open this page. Click to unset." : "Make this the default"}
                      className={preset.isDefault ? "text-amber-500" : "opacity-0 group-hover:opacity-100 text-gray-300 hover:text-amber-500 transition-opacity"}
                    >
                      <Star className="w-3 h-3" fill={preset.isDefault ? "currentColor" : "none"} />
                    </button>
                    <button onClick={() => handleDeletePreset(preset.id)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity -mr-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {hasActiveFilters && (
                  <div className="inline-flex items-center gap-1.5 ml-1">
                    <input
                      placeholder="Save as..."
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                      className="h-7 w-28 px-2 text-xs border border-dashed border-gray-300 rounded-md bg-transparent focus:border-primary focus:outline-none placeholder:text-gray-400"
                    />
                    {presetName.trim() && (
                      <button onClick={handleSavePreset} className="text-xs text-primary font-medium hover:text-primary/80">
                        Save
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
      </div>
    )}
    <div className="bg-white rounded-xl shadow-sm border p-6 flex flex-col min-h-[420px] h-[calc(100vh-160px)] relative">
      {/* Blocking overlay while a bulk assign/unassign is in flight.
          Prevents accidental clicks, shows the lead count so the user knows
          how many are being processed, and warns not to refresh. */}
      {(isAssigning || isUnassigning) && processingLabel && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center rounded-xl bg-white/80 backdrop-blur-sm"
          role="alertdialog"
          aria-live="assertive"
        >
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-10 py-8 shadow-xl">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <div className="text-base font-semibold text-slate-900">
              {processingLabel.action === 'assign' ? 'Assigning' : 'Unassigning'}{' '}
              {processingLabel.count} {processingLabel.count === 1 ? 'lead' : 'leads'}…
            </div>
            <div className="text-xs text-slate-500">
              Please wait — do not refresh or close this tab.
            </div>
          </div>
        </div>
      )}

      {/* Success / skipped banner */}
      {showAssignSuccess && (
        <div
          className="flex flex-col gap-1 px-4 py-2.5 mb-3 rounded-lg border flex-shrink-0"
          style={{
            animation: `assignSuccessFadeSlide ${skippedInfo ? '8' : '2'}s ease-in-out forwards`,
            backgroundColor: skippedInfo ? '#fffbeb' : '#f0fdf4',
            borderColor: skippedInfo ? '#fde68a' : '#bbf7d0',
          }}
        >
          {skippedInfo ? (
            <div className="flex items-center gap-2 text-sm font-medium text-amber-700">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="truncate">
                {assignSuccessCount > 0 && <>{assignSuccessCount.toLocaleString()} assigned · </>}
                {skippedInfo.count.toLocaleString()} already with this agent
                {skippedInfo.names.length > 0 && (
                  <span className="text-amber-600 font-normal">
                    {' '}({skippedInfo.names.slice(0, 3).join(', ')}
                    {skippedInfo.count > Math.min(3, skippedInfo.names.length)
                      ? ` +${(skippedInfo.count - Math.min(3, skippedInfo.names.length)).toLocaleString()} more`
                      : ''})
                  </span>
                )}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm font-medium text-green-700">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              {assignSuccessCount} {assignSuccessCount === 1 ? "lead" : "leads"} assigned successfully
            </div>
          )}
        </div>
      )}
      <style jsx>{`
        @keyframes assignSuccessFadeSlide {
          0% { opacity: 0; transform: translateY(-8px); }
          5% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
      `}</style>
      <div className="flex items-center justify-between gap-3 mb-4 flex-shrink-0 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-600 flex-shrink-0" />
            <h3 className="text-lg font-semibold text-gray-900 whitespace-nowrap">
              {hasActiveFilters ? (
                <>Filtered Leads ({serverTotal.toLocaleString()})</>
              ) : assignmentStatusFilter === 'all' ? (
                <>Agency Leads ({serverTotal.toLocaleString()})</>
              ) : assignmentStatusFilter === 'assigned' ? (
                <>Assigned Leads ({serverTotal.toLocaleString()})</>
              ) : (
                <>Unassigned Leads ({serverTotal.toLocaleString()})</>
              )}
            </h3>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-sm leading-relaxed">
                  <p className="font-medium mb-1">How Lead Assignment Works</p>
                  <p>Select leads and choose an agent from the dropdown to assign them. Once assigned, only the assigned agent can work that lead.</p>
                  <p className="mt-1.5">This total is the assignable book: leads owned by or assigned to this agency's owners and admins, household primaries only, DNC excluded. The Agency Dashboard counts every lead in the agency and its sub-agencies, so it runs higher.</p>
                  <p className="mt-1.5 text-red-400"><span className="font-medium text-red-300">Owners & Agency Admins</span> can assign, reassign, and unassign leads.</p>
                  <p className="mt-1 text-red-400"><span className="font-medium text-red-300">Agents</span> can only view and work on their own assigned leads.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {/* Page-scoped by design; the cards above carry the totals. Saying so
              stops "162,078 — 32 unassigned · 18 assigned" reading as a total. */}
          {leadsToShow.length > 0 && serverTotal > 0 && (
            /* Wraps on phones: the two halves stay whole, the separator drops when it wraps. */
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 ml-7 text-sm tabular-nums text-gray-500">
              <span className="whitespace-nowrap">Showing {(((page - 1) * limit) + 1).toLocaleString()}–{Math.min(page * limit, serverTotal).toLocaleString()} of {serverTotal.toLocaleString()}</span>
              {assignmentStatusFilter === 'all' && (
                <span className="whitespace-nowrap">
                  <span className="text-gray-300 mr-3 hidden sm:inline">&middot;</span>
                  On this page: {unassignedOnPage.toLocaleString()} unassigned, {assignedOnPage.toLocaleString()} assigned
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            onClick={() => setShowSelectImportModal(true)}
            size="sm"
            variant="outline"
            className="flex items-center gap-2"
            title="Mass assign leads from a previous CSV import"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Select List
          </Button>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            size="sm"
            variant={hasActiveFilters ? "default" : "outline"}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-white text-primary rounded-full">
                {(stateFilters.length > 0 ? 1 : 0) + (timezoneFilter ? 1 : 0) + (selectedLeadFilter ? 1 : 0) + (dispositionFilters.length > 0 ? 1 : 0) + (assignmentStatusFilter !== "all" ? 1 : 0) + (agentFilter ? 1 : 0) + (agencyFilters.length > 0 ? 1 : 0) + (listNameFilter ? 1 : 0) + (carrierFilter ? 1 : 0) + (cityFilter ? 1 : 0) + (ageMinStr || ageMaxStr ? 1 : 0) + (debouncedLeadName ? 1 : 0) + (householdPrimariesFilter ? 1 : 0) + (cfValuesActive || cfRangeActive ? 1 : 0)}
              </span>
            )}
          </Button>
          {leadsToShow.length > 0 && (
            <Button
              variant={isAllSelected ? "default" : "outline"}
              size="sm"
              onClick={() => {
                // When in "Deselect All" state, clear the entire selection
                // (including leads selected on other pages) so the visible
                // count matches what the click actually does.
                if (isAllSelected) {
                  setSelectAllAcrossPages(false);
                  setSelectedLeads(new Set());
                } else {
                  handleSelectAll(true);
                }
              }}
              className={`flex items-center gap-2 transition-all ${
                isAllSelected
                  ? "bg-primary text-white hover:bg-primary/90"
                  : isIndeterminate
                    ? "border-primary text-primary hover:bg-primary/5"
                    : "hover:border-primary/50"
              }`}
            >
              {isAllSelected ? (
                // Show total selected across all pages. "isAllSelected" just means
                // the current page is fully checked — the selection itself may
                // span multiple pages.
                <>Deselect All ({selectedLeads.size.toLocaleString()})</>
              ) : isIndeterminate ? (
                <>{selectedLeads.size.toLocaleString()} selected</>
              ) : (
                <>Select All ({leadsToShow.length.toLocaleString()})</>
              )}
            </Button>
          )}
          {/* Select the first N matching leads regardless of page size — page 1
              of the SERVER's ordering, so "first 250" is the same 250 whether
              you're showing 50 or 1000 per page. Assign auto-batches at 1,000,
              so 2,000+ works from here without touching the page size. */}
          {serverTotal > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-600 whitespace-nowrap">Select first</span>
              <input
                aria-label="Number of leads to select"
                type="text"
                inputMode="numeric"
                value={selectFirstDraft}
                disabled={isFetchingAllIds}
                onChange={(e) => setSelectFirstDraft(e.target.value.replace(/[^0-9]/g, ""))}
                onFocus={(e) => e.currentTarget.select()}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  e.currentTarget.blur();
                  const parsed = Number.parseInt(selectFirstDraft, 10);
                  if (Number.isFinite(parsed) && parsed > 0) void fetchAllLeadIds(parsed);
                }}
                className="h-9 w-16 px-2 text-sm text-center font-medium rounded-md border border-gray-300 bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={isFetchingAllIds || !(Number.parseInt(selectFirstDraft, 10) > 0)}
                onClick={() => {
                  const parsed = Number.parseInt(selectFirstDraft, 10);
                  if (Number.isFinite(parsed) && parsed > 0) void fetchAllLeadIds(parsed);
                }}
                className="flex items-center gap-1.5"
              >
                {isFetchingAllIds ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ListChecks className="w-3.5 h-3.5" />
                )}
                Select
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="mb-3 flex flex-wrap items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-500">Active filters:</span>
          {stateFilters.length > 0 && (
            <button onClick={() => { setStateFilters([]); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              {stateFilters.length === 1 ? "State" : "States"}: {stateFilters.slice(0, 8).join(", ")}{stateFilters.length > 8 ? ` +${stateFilters.length - 8}` : ""} <X className="w-3 h-3" />
            </button>
          )}
          {timezoneFilter && (
            <button onClick={() => { setTimezoneFilter(undefined); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              Time Zone: {timezoneFilter} <X className="w-3 h-3" />
            </button>
          )}
          {listNameFilter && (
            <button onClick={() => { setListNameFilter(undefined); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              List: {listNameFilter.length > 20 ? listNameFilter.slice(0, 20) + '...' : listNameFilter} <X className="w-3 h-3" />
            </button>
          )}
          {/* One removable chip PER selected stage, so an owner can drop a single
              stage from the group without clearing the whole filter. */}
          {dispositionFilters.map((d) => (
            <button
              key={d}
              onClick={() => { setDispositionFilters(dispositionFilters.filter((x) => x !== d)); setPage(1); }}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100"
            >
              Status: {LEAD_DISPOSITIONS.find((x) => x.value === d)?.label || d} <X className="w-3 h-3" />
            </button>
          ))}
          {assignmentStatusFilter !== "all" && (
            <button onClick={() => { setAssignmentStatusFilter("all"); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              {assignmentStatusFilter === "assigned" ? "Assigned Only" : "Unassigned Only"} <X className="w-3 h-3" />
            </button>
          )}
          {agentFilter && (
            <button onClick={() => { setAgentFilter(undefined); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              Agent: {(availableAgents || []).find((a: any) => (a.agentId || a.id) === agentFilter)?.agentName || (availableAgents || []).find((a: any) => (a.agentId || a.id) === agentFilter)?.name || 'Unknown'} <X className="w-3 h-3" />
            </button>
          )}
          {agencyFilters.map((id) => (
            <button
              key={id}
              onClick={() => { setAgencyFilters(agencyFilters.filter((x) => x !== id)); setPage(1); }}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100"
            >
              Agency: {uniqueAgencies.find((a: any) => a.id === id)?.name || 'Unknown'} <X className="w-3 h-3" />
            </button>
          ))}
          {debouncedLeadName && !selectedLeadFilter && (
            <button onClick={() => { setDebouncedLeadName(undefined); setLeadNameSearch(""); setPage(1); if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              Name: {`"${debouncedLeadName}"`} <X className="w-3 h-3" />
            </button>
          )}
          {carrierFilter && (
            <button onClick={() => { setCarrierFilter(undefined); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              Carrier: {carrierLabel(carrierFilter)} <X className="w-3 h-3" />
            </button>
          )}
          {cityFilter && (
            <button onClick={() => { setCityFilter(undefined); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              City: {cityFilter} <X className="w-3 h-3" />
            </button>
          )}
          {(ageMinStr !== "" || ageMaxStr !== "") && (
            <button onClick={() => { setAgeMinStr(""); setAgeMaxStr(""); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              Age: {ageMinStr && ageMaxStr ? `${ageMinStr}–${ageMaxStr}` : ageMinStr ? `≥ ${ageMinStr}` : `≤ ${ageMaxStr}`} <X className="w-3 h-3" />
            </button>
          )}
          {householdPrimariesFilter && (
            <button onClick={() => { setHouseholdPrimariesFilter(false); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full hover:bg-purple-100">
              Household Primaries <X className="w-3 h-3" />
            </button>
          )}
          {cfRangeActive && (
            <button onClick={() => { setCfMinStr(""); setCfMaxStr(""); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              {customFieldKey}: {cfMin != null && cfMax != null ? `${cfMin}–${cfMax}` : cfMin != null ? `≥ ${cfMin}` : `≤ ${cfMax}`} <X className="w-3 h-3" />
            </button>
          )}
          {cfValuesActive && (
            <button onClick={() => { setCustomFieldValues([]); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              {customFieldKey}: {customFieldValues.slice(0, 3).join(", ")}{customFieldValues.length > 3 ? ` +${customFieldValues.length - 3}` : ""} <X className="w-3 h-3" />
            </button>
          )}
          {selectedLeadFilter && (
            <button onClick={() => { setSelectedLeadFilter(undefined); setLeadNameSearch(""); setDebouncedLeadName(undefined); setPage(1); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100">
              Lead: {rawQueryLeads.find((l: Lead) => l.id === selectedLeadFilter)?.name || 'Selected'} <X className="w-3 h-3" />
            </button>
          )}
          <button onClick={clearFilters} className="text-xs text-red-500 hover:text-red-700 underline ml-1">
            Clear all
          </button>
        </div>
      )}

      {/* Select All Across Pages Banner */}
      {isAllSelected && serverTotal > leadsToShow.length && (
        <div className="mb-3 flex items-center gap-2.5 px-3 py-2 rounded-md border border-gray-200 bg-gray-50/80 text-[13px] text-gray-700 flex-shrink-0">
          <CheckCircle2 className="h-4 w-4 text-gray-500 flex-shrink-0" strokeWidth={1.75} />
          {selectAllAcrossPages ? (
            <>
              <span className="min-w-0 flex-1">
                <span className="tabular-nums font-semibold text-gray-900">{selectedLeads.size.toLocaleString()}</span>
                <span className="text-gray-500"> leads selected across all pages</span>
              </span>
              <button
                onClick={() => {
                  setSelectAllAcrossPages(false);
                  setSelectedLeads(new Set(leadsToShow.map(lead => lead.id)));
                }}
                className="text-[12px] font-medium text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
              >
                Clear selection
              </button>
            </>
          ) : (
            <>
              <span className="min-w-0 flex-1">
                <span className="tabular-nums font-semibold text-gray-900">{leadsToShow.length}</span>
                <span className="text-gray-500"> on this page selected</span>
              </span>
              <button
                onClick={() => fetchAllLeadIds()}
                disabled={isFetchingAllIds}
                className={[
                  "group inline-flex items-center gap-1.5 flex-shrink-0",
                  "h-7 px-2.5 rounded-md",
                  "bg-primary text-primary-foreground text-[11px] font-medium tracking-wide",
                  "shadow-[0_1px_0_0_rgba(255,255,255,0.14)_inset,0_1px_2px_0_rgba(0,0,0,0.12)]",
                  "hover:bg-primary/90 active:scale-[0.98]",
                  "transition-[background-color,transform] duration-150",
                  "disabled:opacity-50 disabled:cursor-wait disabled:active:scale-100",
                ].join(" ")}
              >
                {isFetchingAllIds ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Selecting</span>
                  </>
                ) : serverTotal > MAX_BULK_SELECT ? (
                  <>
                    <ListChecks className="h-3 w-3 opacity-90 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
                    <span>Select <span className="tabular-nums font-semibold">{MAX_BULK_SELECT.toLocaleString()}</span> of <span className="tabular-nums opacity-75">{serverTotal.toLocaleString()}</span></span>
                  </>
                ) : (
                  <>
                    <ListChecks className="h-3 w-3 opacity-90 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
                    <span>Select all <span className="tabular-nums font-semibold">{serverTotal.toLocaleString()}</span></span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* Lead Health Stats */}
      {leadsToShow.length > 0 && (() => {
        const neverCalled = leadsToShow.filter((l: Lead) => !l.lastCall).length;
        const stale30 = leadsToShow.filter((l: Lead) => {
          const days = getDaysAgo(l.lastCall || null);
          return days !== null && days > 30;
        }).length;
        const fresh = leadsToShow.filter((l: Lead) => {
          const days = getDaysAgo(l.lastCall || null);
          return days !== null && days <= 7;
        }).length;
        if (neverCalled === 0 && stale30 === 0 && fresh === 0) return null;
        return (
          <div className="mb-3 flex items-center gap-2 flex-shrink-0">
            {neverCalled > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 border-l-2 border-red-400 bg-white rounded-r text-xs">
                <span className="tabular-nums font-semibold text-red-600">{neverCalled}</span>
                <span className="text-gray-500">never called</span>
              </div>
            )}
            {stale30 > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 border-l-2 border-amber-400 bg-white rounded-r text-xs">
                <span className="tabular-nums font-semibold text-amber-600">{stale30}</span>
                <span className="text-gray-500">no call in 30+ days</span>
              </div>
            )}
            {fresh > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 border-l-2 border-green-400 bg-white rounded-r text-xs">
                <span className="tabular-nums font-semibold text-green-600">{fresh}</span>
                <span className="text-gray-500">called this week</span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Assignment Actions - shows when leads are selected */}
      {selectedLeads.size > 0 && (
        <div className="mb-4 flex-shrink-0">
          <AssignmentActions
            onAssign={handleAssign}
            onUnassign={handleUnassign}
            onPreviewUnassign={previewUnassign}
            selectedLeadsCount={selectedLeads.size}
            selectedAssignedCount={selectedAssignedCount}
            selectedUnassignedCount={selectedUnassignedCount}
            isAssigning={isAssigning}
            isUnassigning={isUnassigning}
            hasAssignedLeads={hasAssignedLeads}
            hasUnassignedLeads={hasUnassignedLeads}
          />
        </div>
      )}

      <div className="space-y-3 flex-1 min-h-0 overflow-y-auto">
        {error && leadsToShow.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
            <p className="text-gray-700">
              {/timeout/i.test(String((error as any)?.message || ""))
                ? "These filters took too long to load, so we couldn't check for matching leads."
                : "Couldn't load leads for these filters."}
            </p>
            <p className="text-sm text-gray-500">This is not the same as no matches. Try again, or add a state or list filter to narrow it.</p>
            <Button variant="outline" size="sm" onClick={() => refetchUnassigned()} disabled={isFetching}>
              {isFetching ? "Retrying…" : "Try again"}
            </Button>
          </div>
        ) : leadsToShow.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-600">
              {hasActiveFilters ? "No leads match the selected filters" : "No unassigned leads found"}
            </p>
          </div>
        )}
        {leadsToShow.length > 0 && leadsToShow.map((lead) => {
          const isAssigned = !!lead.assignedToUserId;
          const leadAge = getDaysAgo(lead.createdAt);
          const ageBadge = getAgeBadge(leadAge);
          const callBadge = getLastCallBadge(lead.lastCall, lead.createdAt);
          return (
            <div
              key={lead.id}
              onClick={() => handleLeadSelection(lead.id, !selectedLeads.has(lead.id))}
              className={`flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer ${
                isAssigned ? 'border-primary/30 bg-primary/5' : 'border-gray-200'
              }`}
            >
              <input
                checked={selectedLeads.has(lead.id)}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                onChange={(e) => {
                  e.stopPropagation();
                  handleLeadSelection(lead.id, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                type="checkbox"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {lead.name}
                  </p>
                  {isAgencyAdmin && lead.uploadedByName && lead.uploadedByName !== user?.user?.name && (
                    <span className="text-xs text-gray-400">Uploaded by {lead.uploadedByName}</span>
                  )}
                  {isAssigned && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                      Assigned to {lead.assignedToName || 'Agent'}
                      {getAgentAgencyName(lead.assignedToUserId) && (
                        <span className="text-primary"> - {getAgentAgencyName(lead.assignedToUserId)}</span>
                      )}
                    </span>
                  )}
                  {(lead as any).activityVisibleAfter && (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await handleApiCall(async () =>
                                  api.post({
                                    url: getApiUrl('/lead-assignment/restore-activity'),
                                    body: { leadIds: [lead.id] },
                                    auth: true,
                                  })
                                );
                                toast({ title: "Activity restored", description: `Previous activity for ${lead.name} is now visible.` });
                                refetchUnassigned();
                              } catch {
                                toast({ title: "Error", description: "Failed to restore activity", variant: "destructive" });
                              }
                            }}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-600 rounded hover:bg-amber-100 transition-colors"
                          >
                            <Sparkles className="w-2.5 h-2.5" />
                            Fresh — click to restore
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          Previous activity is hidden. Click to restore all notes, calls, and activity.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {!isAssigned && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                      Unassigned
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                  <span>{formatPhone(lead.phone)}</span>
                  {lead.state && (
                    <>
                      <span>•</span>
                      <span>{lead.state}</span>
                    </>
                  )}
                  {lead.hasHousehold && (lead.householdMemberCount ?? 0) > 0 && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1 text-purple-600">
                        <Users className="h-3 w-3" />
                        {lead.householdMemberCount}
                      </span>
                    </>
                  )}
                  {lead.listName && (
                    <>
                      <span>•</span>
                      <span className="text-xs text-gray-400 truncate max-w-[120px]" title={lead.listName}>{lead.listName}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${getCustomPipelineColor(lead.leadStat || '') ? 'text-white' : getStatusColor(lead.leadStat || 'new')}`}
                  style={getCustomPipelineColor(lead.leadStat || '') ? { backgroundColor: getCustomPipelineColor(lead.leadStat || '') } : undefined}
                >
                  {getStatusDisplayName(lead.leadStat || 'new')}
                </span>
                <div className="flex items-center gap-1.5">
                  <LeadRowTags hasPolicy={lead.hasPolicy} nextAppointmentAt={lead.nextAppointmentAt} nextAppointmentWith={lead.nextAppointmentWith} />
                  {ageBadge && (
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded ${ageBadge.className}`} title={`Lead created ${leadAge}d ago`}>
                      <Calendar className="h-2.5 w-2.5" />
                      {ageBadge.text}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded ${callBadge.className}`} title={callBadge.text}>
                    {callBadge.icon === 'warning' ? <PhoneOff className="h-2.5 w-2.5" /> : <Clock className="h-2.5 w-2.5" />}
                    {callBadge.text}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {serverTotal > 0 && (
        <div className="flex items-center justify-between pt-3 border-t mt-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {((page - 1) * limit) + 1}-{Math.min(page * limit, serverTotal)} of {serverTotal.toLocaleString()}
            </span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="px-2 py-1 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || isFetching}
              className="px-3 py-1 text-sm font-medium rounded-md border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent"
            >
              Prev
            </button>
            <span className="text-sm text-muted-foreground">
              {isFetching ? <Loader2 className="w-3 h-3 animate-spin inline" /> : page}/{serverTotalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(serverTotalPages, p + 1))}
              disabled={page >= serverTotalPages || isFetching}
              className="px-3 py-1 text-sm font-medium rounded-md border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Select Import List Modal */}
      <SelectImportListModal
        isOpen={showSelectImportModal}
        onClose={() => setShowSelectImportModal(false)}
        onAssignmentComplete={() => {
          // Stays open on purpose — see the matching comment on the lead-assignment page.
          // Assigning a run of lists shouldn't require reopening the modal each time.
          refetchUnassigned();
          refetchAgents();
        }}
      />
    </div>
    </div>
  );
};